// src/services/matrixService.js
// Matrix SDK service - centralized Matrix client management and room operations

import {
  createClient,
  ClientEvent,
  MatrixEventEvent,
  IndexedDBCryptoStore,
} from 'matrix-js-sdk';
import { CryptoEvent, OnlySignedDevicesIsolationMode, AllDevicesIsolationMode } from 'matrix-js-sdk/lib/crypto-api';
import recoveryEphemeralStore from './recoveryEphemeralStore';
import { isSsoLoggedInSession } from './authCredentialService';
import logger from '../utils/logger';
import { buildTIMUserAgent, timFetch } from '../utils/timUserAgent';
import { decryptMediaBlob } from '../utils/encryptionUtils';
import { isSupersededInvalidation } from '../utils/sessionInvalidation';
import { uploadMedia } from './matrixMediaUpload';
import * as credentialStore from './credentialStore';
import { configureSdkLogging } from './sdkLogConfig';

// Quiet the matrix-js-sdk + rust-crypto WASM console logging per the app's log-level policy
// (NED-4003). Runs at module load — before any createClient()/initRustCrypto() — so the
// leveled SDK logger is in place when the WASM crypto store is opened. matrixAuthService.js
// makes the same call; both are intentional and idempotent (whichever service loads first wins).
configureSdkLogging();

/**
 * Matrix Service - Handles all Matrix SDK operations
 * Manages client lifecycle, room operations, and message handling
 * 
 * Note: This service no longer extends EventEmitter to avoid redundant events.
 * The Matrix SDK already provides all necessary events through the client.
 * Components should use hooks to subscribe to client events directly.
 */
class MatrixService {
  // Timeout for crypto initialization to prevent indefinite hangs (30 seconds)
  static CRYPTO_INIT_TIMEOUT_MS = 30000;
  // Wait time for crypto shutdown to release IndexedDB connections (1.5 seconds)
  static CRYPTO_SHUTDOWN_WAIT_MS = 1500;

  constructor() {
    this.client = null;
    this.isInitialized = false;
    this.isSyncing = false;
    this.syncState = null;
    this.isSyncStarted = false; // Track if sync has been started to prevent double-starts

    // Temporary key for verification purposes only
    this._temporaryVerificationKey = null;

    // Timer for throttled room refresh after decryption
    this.refreshTimer = null;

    // Timer for sync state check
    this.syncStateCheckTimer = null;

    // Throttling for presence updates to prevent 429 rate limit errors
    this.lastPresenceUpdate = 0;
    this.minPresenceInterval = 30000; // 30 seconds minimum between updates (increased from 10s to avoid 429 errors)
    this.pendingPresenceTimer = null; // Timer for deferred presence updates
    this.presenceRetryCount = 0; // Track retry attempts for exponential backoff
    this.maxPresenceRetries = 3; // Maximum number of retries
    this.presenceRetryTimeoutId = null; // Timeout ID for presence retry (for cleanup)

    this.dehydrationEnabled = false; // Flag to disable dehydration if needed
    this.isDehydrating = false; // Flag to prevent concurrent dehydration attempts

    // Timer IDs for dehydration-related delayed operations (for cleanup)
    this._dehydrationEventTimer = null; // 500ms delay after event triggers
    this._dehydrationBackupTimer = null; // 1000ms delay after key backup setup

    // Handler reference for page visibility API
    this._visibilityChangeHandler = null;

    // Presence configuration
    this.sendPresenceUpdates = true;
    // Logout prevention flag to avoid duplicate logout attempts
    this._loggingOut = false;

    // Pending DM rooms cache to prevent race condition during invite acceptance
    // Maps roomId → { timestamp, timerId } for recently accepted DM invites
    // This ensures isDMRoom() returns true during the gap between membership change
    // and m.direct account data update arriving from the server
    this.pendingDMRooms = new Map();

    // Periodic cleanup interval to prevent memory leaks (starts when client is initialized)
    this.pendingDMCleanupInterval = null; // Don't start yet
  }

  /**
   * Reset device keys on the server (use when getting key upload conflicts)
   */
  async resetDeviceKeys() {
    try {
      logger.debug('Attempting to reset device keys on server');

      // Get current credentials to make direct API calls
      const creds = credentialStore.readCredentials();
      if (!creds) {
        logger.warn('No credentials found, cannot reset device keys');
        return false;
      }

      const deviceId = creds.deviceId;
      const accessToken = creds.accessToken;
      const baseUrl = creds.homeserverUrl;

      // First, try to delete existing one-time keys
      logger.debug('Attempting to clear one-time keys from server');

      // Upload empty one-time keys to clear them
      const keysUploadUrl = `${baseUrl}/_matrix/client/v3/keys/upload`;
      try {
        const emptyKeysResponse = await timFetch(keysUploadUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            one_time_keys: {},  // Empty one-time keys to clear server state
          }),
        });

        if (emptyKeysResponse.ok) {
          const result = await emptyKeysResponse.json();
          logger.info('Cleared one-time keys from server:', result);
        }
      } catch (error) {
        logger.warn('Could not clear one-time keys:', error.message);
      }

      // Optionally delete the entire device (more drastic)
      if (localStorage.getItem('matrixForceDeviceReset') === 'true') {
        const deleteUrl = `${baseUrl}/_matrix/client/v3/devices/${deviceId}`;

        try {
          const response = await timFetch(deleteUrl, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              auth: {
                type: 'm.login.password',
                user: creds.userId,
                password: localStorage.getItem('matrixTempPassword') || '',
              },
            }),
          });

          if (response.ok) {
            logger.info('Device deleted from server successfully');
            localStorage.removeItem('matrixForceDeviceReset');
            localStorage.removeItem('matrixTempPassword');
            return true;
          } else {
            logger.warn('Could not delete device from server:', response.status, response.statusText);
          }
        } catch (fetchError) {
          logger.warn('Network error deleting device:', fetchError.message);
        }
      }

      return false;
    } catch (error) {
      logger.warn('Could not reset device keys:', error.message);
      return false;
    }
  }

  /**
   * Check if dehydration is supported by server and SDK
   * @returns {Promise<boolean>} True if dehydration is supported
   */
  async isDehydrationSupported() {
    if (!this.client) {
      return false;
    }

    try {
      const crypto = this.client.getCrypto();
      if (!crypto) {
        logger.debug('Dehydration: Crypto not available');
        return false;
      }

      // Check if crypto API has the method (SDK version check)
      if (typeof crypto.isDehydrationSupported !== 'function') {
        logger.debug('Dehydration: SDK does not support dehydration API');
        return false;
      }

      // Check server support
      const supported = await crypto.isDehydrationSupported();
      logger.info('Dehydration support check:', supported);
      return supported;
    } catch (error) {
      logger.warn('Failed to check dehydration support:', error);
      return false;
    }
  }

  /**
   * Check if dehydration prerequisites are met (cross-signing + key backup enabled)
   * Centralized method to avoid duplication across trigger, check, and setup functions
   * @private
   * @returns {Promise<{hasCrossSigningKeys: boolean, isBackupEnabled: boolean}>}
   */
  async checkDehydrationPrerequisites() {
    const crypto = this.client?.getCrypto();
    if (!crypto) {
      return { hasCrossSigningKeys: false, isBackupEnabled: false };
    }

    let hasCrossSigningKeys = false;
    let isBackupEnabled = false;

    try {
      const status = await crypto.getCrossSigningStatus();
      hasCrossSigningKeys = status?.publicKeysOnDevice ||
                            status?.privateKeysCachedLocally ||
                            status?.privateKeysInSecretStorage;
    } catch (error) {
      logger.debug('Dehydration: Failed to check cross-signing status:', error);
    }

    try {
      const backupInfo = await crypto.getKeyBackupInfo();
      const activeVersion = await crypto.getActiveSessionBackupVersion();
      isBackupEnabled = !!(backupInfo && activeVersion);
    } catch (error) {
      logger.debug('Dehydration: Failed to check backup status:', error);
    }

    return { hasCrossSigningKeys, isBackupEnabled };
  }

  /**
   * Setup event listeners to trigger dehydration when prerequisites are met
   * Listens to multiple crypto events that indicate recovery/setup completion
   * @private
   */
  setupDehydrationTriggers() {
    if (this._dehydrationTriggersSetup) {
      logger.debug('Dehydration: Triggers already set up');
      return;
    }

    this._dehydrationTriggersSetup = true;
    logger.info('Dehydration: Setting up event-based triggers');

    const checkAndTrigger = async (eventName) => {
      logger.debug(`Dehydration: ${eventName} event fired, checking prerequisites`);

      const { hasCrossSigningKeys, isBackupEnabled } = await this.checkDehydrationPrerequisites();
      logger.debug('Dehydration: Prerequisites check:', { hasCrossSigningKeys, isBackupEnabled });

      if (hasCrossSigningKeys && isBackupEnabled) {
        logger.info('Dehydration: Prerequisites met via event trigger, starting dehydration');
        
        // Remove all listeners
        this.cleanupDehydrationTriggers();
        
        // Delay to ensure cross-signing identity and keys have synced
        // Store timer ID for cleanup if client is destroyed before timer fires
        // CRITICAL: Increased from 500ms to 1000ms to handle slow networks/server load
        this._dehydrationEventTimer = setTimeout(async () => {
          this._dehydrationEventTimer = null;
          // Guard against destroyed client
          if (!this.client) {
            logger.debug('Dehydration: Skipping event-triggered setup - client destroyed');
            return;
          }
          try {
            await this.setupDehydration();
          } catch (error) {
            logger.warn('Dehydration: Event-triggered setup failed:', error.message);
          }
        }, 1000);
      }
    };

    // Listen to multiple events that could indicate recovery completion
    
    // 1. User trust status changed (cross-signing setup)
    this._trustStatusHandler = (userId) => {
      if (userId === this.client.getUserId()) {
        checkAndTrigger('UserTrustStatusChanged');
      }
    };
    this.client.on(CryptoEvent.UserTrustStatusChanged, this._trustStatusHandler);
    
    // 2. Key backup status changed (backup enabled/restored)
    this._backupStatusHandler = () => checkAndTrigger('KeyBackupStatus');
    this.client.on(CryptoEvent.KeyBackupStatus, this._backupStatusHandler);
    
    // 3. Account data changed (SSSS updates, m.cross_signing keys stored)
    this._accountDataHandler = (event) => {
      const type = event.getType();
      // Check for secret storage / cross-signing / key-backup related account data
      if (this._isCrossSigningSecretAccountData(type)) {
        logger.debug(`Dehydration: Account data event: ${type}`);
        checkAndTrigger('AccountData');
      }
    };
    this.client.on(ClientEvent.AccountData, this._accountDataHandler);
    
    logger.debug('Dehydration: Event listeners registered (UserTrustStatusChanged, KeyBackupStatus, AccountData)');
  }

  /**
   * Clean up dehydration event listeners and pending timers
   * @private
   */
  cleanupDehydrationTriggers() {
    // Clear pending dehydration timers first (prevents callbacks on destroyed client)
    if (this._dehydrationEventTimer) {
      clearTimeout(this._dehydrationEventTimer);
      this._dehydrationEventTimer = null;
      logger.debug('Dehydration: Cleared pending event timer');
    }
    if (this._dehydrationBackupTimer) {
      clearTimeout(this._dehydrationBackupTimer);
      this._dehydrationBackupTimer = null;
      logger.debug('Dehydration: Cleared pending backup timer');
    }

    if (!this._dehydrationTriggersSetup) return;

    logger.debug('Dehydration: Cleaning up event listeners');

    if (this._trustStatusHandler) {
      this.client.off(CryptoEvent.UserTrustStatusChanged, this._trustStatusHandler);
      this._trustStatusHandler = null;
    }

    if (this._backupStatusHandler) {
      this.client.off(CryptoEvent.KeyBackupStatus, this._backupStatusHandler);
      this._backupStatusHandler = null;
    }

    if (this._accountDataHandler) {
      this.client.off(ClientEvent.AccountData, this._accountDataHandler);
      this._accountDataHandler = null;
    }

    this._dehydrationTriggersSetup = false;
    logger.debug('Dehydration: Event listeners cleaned up');
  }

  /**
   * Manually trigger dehydration check (called by recovery flow)
   * Use this after recovery key processing completes
   * @returns {Promise<void>}
   */
  async triggerDehydrationCheck() {
    logger.info('Dehydration: Manual trigger requested');

    const { hasCrossSigningKeys, isBackupEnabled } = await this.checkDehydrationPrerequisites();
    logger.info('Dehydration: Manual trigger prerequisites:', { hasCrossSigningKeys, isBackupEnabled });

    if (hasCrossSigningKeys && isBackupEnabled) {
      logger.info('Dehydration: Manual trigger successful, starting dehydration');
      this.cleanupDehydrationTriggers();
      await this.setupDehydration();
    } else {
      logger.info('Dehydration: Prerequisites not met on manual trigger, keeping listeners active');
    }
  }

    /**
   * Setup device dehydration for backup and recovery
   * Called after crypto initialization and key backup setup
   * @param {Object} options - Optional configuration
   * @param {boolean} options.forceNewKey - Force creating a new dehydration key (use after recovery key reset)
   * @returns {Promise<void>}
   */
  async setupDehydration(options = {}) {
    const { forceNewKey = false } = options;
    logger.info('Dehydration: setupDehydration() called', { forceNewKey });

    // Check feature flag
    const featureFlagEnabled = process.env.REACT_APP_ENABLE_DEHYDRATION === 'true';
    if (!featureFlagEnabled) {
      logger.debug('Dehydration: Feature disabled via REACT_APP_ENABLE_DEHYDRATION');
      return;
    }

    // Prevent concurrent dehydration attempts
    if (this.isDehydrating) {
      logger.debug('Dehydration: Already in progress, skipping');
      return;
    }

    try {
      this.isDehydrating = true;

      // Cancel any pending dehydration timers from event triggers
      // This prevents duplicate dehydration attempts if setupDehydration is called directly
      this.cleanupDehydrationTriggers();

      const crypto = this.client.getCrypto();
      if (!crypto) {
        logger.debug('Dehydration: Crypto not available');
        return;
      }

      // If forcing new key (recovery key reset), stop existing dehydration first
      // This prevents null pointer errors during logout by cleaning up stale references
      if (forceNewKey && this.dehydrationEnabled) {
        try {
          if (typeof crypto.stopDehydration === 'function') {
            logger.info('Dehydration: Stopping existing dehydration before creating new device');
            await crypto.stopDehydration();
            this.dehydrationEnabled = false;
            // Small delay to ensure clean state
            await new Promise(resolve => setTimeout(resolve, 300));
          }
        } catch (stopError) {
          logger.debug('Dehydration: Stop failed (may not have been running):', stopError.message);
        }
      }

      // Check if dehydration is supported
      const supported = await this.isDehydrationSupported();
      if (!supported) {
        logger.info('Dehydration: Not supported by server or SDK');
        return;
      }

      // Skip cross-signing check if forcing new key (we know it's already set up from reset flow)
      if (!forceNewKey) {
        // Check prerequisites using centralized method
        const { hasCrossSigningKeys } = await this.checkDehydrationPrerequisites();
        logger.debug('Dehydration: Cross-signing check:', { hasCrossSigningKeys });

        if (!hasCrossSigningKeys) {
          logger.info('Dehydration: Cross-signing not initialized yet, listening for setup event');
          // Instead of polling, listen for the event that fires when cross-signing is ready
          this.setupDehydrationTriggers();
          return;
        }
      }

      // Start dehydration with setup-friendly options
      logger.info('Dehydration: Starting dehydration...');

      // Check if dehydration key is already cached in SDK (subsequent launches)
      const keyIsCached = await crypto.isDehydrationKeyStored?.() ?? false;

      // Check if user has provided recovery key (via manual entry or bootstrap)
      // This key is stored in recoveryEphemeralStore and will be provided via getSecretStorageKey callback
      // Use getKeyAsync() to ensure session storage is loaded (consistent with line 960 callback pattern)
      const ephemeralKey = await recoveryEphemeralStore.getKeyAsync();
      const hasEphemeralKey = ephemeralKey !== null;

      // Determine if we can attempt rehydration:
      // - If SDK has cached key: yes (from previous successful dehydration)
      // - If user provided key via ephemeral store: yes (our callback will provide it)
      // - If temporary verification key is set: yes (recovery key verification is in progress,
      //   the getSecretStorageKey callback will use it)
      // - Otherwise: no (would cause "getSecretStorageKey callback returned falsey" error)
      const hasTempVerificationKey = !!this._temporaryVerificationKey;
      const canRehydrate = keyIsCached || hasEphemeralKey || hasTempVerificationKey;

      // If no recovery key is available, defer to avoid overwriting an existing
      // dehydrated device (e.g., one created by iOS) before we can rehydrate it.
      // startDehydration() always creates a new device at the end, which would
      // replace the existing one on the server and lose its to-device messages.
      // Dehydration requires SSSS access anyway, so deferring is always safe.
      // The recovery flow's triggerDehydrationCheck() will re-invoke once the key is available.
      if (!canRehydrate && !forceNewKey) {
        logger.info('Dehydration: No key available for rehydration, deferring to preserve existing device');
        this.setupDehydrationTriggers();
        return;
      }

      const opts = {
        // Force new key after recovery key reset, otherwise create only if not cached
        createNewKey: forceNewKey || !keyIsCached,
        onlyIfKeyCached: false,       // Allow key creation/storage (required for first setup)
        // Only rehydrate if key is available - prevents "getSecretStorageKey returned falsey" error
        // After SAS verification without recovery key entry, neither source has the key
        rehydrate: !forceNewKey && canRehydrate,
      };

      logger.debug('Dehydration: Using options:', { ...opts, keyIsCached, hasEphemeralKey, hasTempVerificationKey, forceNewKey });
      await crypto.startDehydration(opts);
      
      this.dehydrationEnabled = true;
      logger.info('Dehydration: Started successfully');
    } catch (error) {
      // Don't throw - dehydration is optional
      logger.warn('Dehydration: Failed to start:', error.message);
      
      // Specific error handling
      if (error.message?.includes('cross-signing') || 
          error.message?.includes('secret storage') ||
          error.message?.includes('not ready')) {
        // logger.debug('Dehydration: Prerequisites not met - scheduling retry');
        // this.scheduleDeferredDehydration();
        logger.debug('Dehydration: Prerequisites not met - listening for setup event');
        // Listen for events instead of polling
        this.setupDehydrationTriggers();
      } else if (error.message?.includes('M_UNRECOGNIZED')) {
        logger.debug('Dehydration: Server does not support MSC3814');
      }
    } finally {
      this.isDehydrating = false;
    }
  }
  /**
   * Set temporary verification key for SSSS callback during verification
   * This key is used only during the verification process and is cleared afterward
   * @param {Uint8Array} keyBytes - The recovery key bytes to use temporarily
   */
  setTemporaryVerificationKey(keyBytes) {
    // Make a copy to avoid mutating the input
    this._temporaryVerificationKey = keyBytes ? new Uint8Array(keyBytes) : null;
  }

  /**
   * Clear the temporary verification key
   */
  clearTemporaryVerificationKey() {
    if (this._temporaryVerificationKey) {
      // Zero out the key bytes for security
      if (this._temporaryVerificationKey.fill) {
        this._temporaryVerificationKey.fill(0);
      }
      this._temporaryVerificationKey = null;
    }
  }

  /**
   * Clear ALL Matrix-related crypto stores (for dev/testing)
   * Now with proper transaction handling to avoid InvalidStateError
   */
  async clearAllMatrixStores() {
    try {
      // Stop the client first if it's running to close all connections
      if (this.client && this.isSyncing) {
        logger.debug('Stopping client before clearing stores');
        this.client.stopClient();
        this.isSyncing = false;
        // Wait for client to fully stop
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      // Get all databases
      const databases = await window.indexedDB.databases();

      // Find all Matrix-related databases
      const matrixDatabases = databases.filter(db =>
        db.name && db.name.includes('matrix-js-sdk'),
      );

      if (matrixDatabases.length === 0) {
        logger.debug('No Matrix databases found to clear');
        return;
      }

      logger.info('Found Matrix databases to clear:', matrixDatabases.map(db => db.name));

      // Delete each Matrix database with proper error handling and timeout
      const deletePromises = matrixDatabases.map(db => {
        return new Promise((resolve) => {
          const timeout = setTimeout(() => {
            logger.warn(`Database ${db.name} deletion timed out after 5s`);
            resolve(false);
          }, 5000);

          try {
            const deleteReq = window.indexedDB.deleteDatabase(db.name);

            deleteReq.onsuccess = () => {
              clearTimeout(timeout);
              logger.info(`Cleared database: ${db.name}`);
              resolve(true);
            };

            deleteReq.onerror = () => {
              clearTimeout(timeout);
              logger.warn(`Could not clear database ${db.name}:`, deleteReq.error);
              resolve(false);
            };

            deleteReq.onblocked = () => {
              clearTimeout(timeout);
              logger.warn(`Database ${db.name} deletion blocked - connections still open`);
              resolve(false);
            };
          } catch (e) {
            clearTimeout(timeout);
            logger.warn(`Error initiating database deletion for ${db.name}:`, e);
            resolve(false);
          }
        });
      });

      const results = await Promise.all(deletePromises);
      const successCount = results.filter(r => r).length;
      logger.info(`Cleared ${successCount}/${matrixDatabases.length} Matrix crypto stores`);

    } catch (error) {
      logger.error('Error clearing Matrix stores:', error);
      // Don't throw - just log the error
    }
  }

  /**
   * Proactively clear ALL crypto databases before login
   * This prevents crypto init from hanging on stale data when
   * a user logs back in with a new device after recovery key reset.
   *
   * For a fresh login, there's no reason to keep any old crypto databases:
   * - Old device keys don't match the new device
   * - Stale cross-signing/backup data causes hangs
   * - New session will create fresh crypto stores
   */
  async clearAllCryptoDatabasesBeforeLogin() {
    try {
      const databases = await window.indexedDB.databases();

      // Find ALL Matrix crypto databases (both JS SDK and Rust SDK)
      const cryptoDatabases = databases.filter(db => {
        if (!db.name) return false;

        // Rust SDK store (double colon)
        if (db.name === 'matrix-js-sdk::matrix-sdk-crypto') return true;

        // JS SDK stores (single colon with :crypto:)
        if (db.name.startsWith('matrix-js-sdk:crypto:')) return true;

        return false;
      });

      if (cryptoDatabases.length === 0) {
        logger.debug('No crypto databases to clean up before login');
        return;
      }

      logger.info('Proactively clearing ALL crypto databases before login:', cryptoDatabases.map(db => db.name));

      // Delete with timeout to avoid hanging
      const deletePromises = cryptoDatabases.map(db => {
        return new Promise((resolve) => {
          const timeout = setTimeout(() => {
            logger.warn(`Proactive cleanup: ${db.name} deletion timed out after 3s`);
            resolve(false);
          }, 3000);

          try {
            const deleteReq = window.indexedDB.deleteDatabase(db.name);

            deleteReq.onsuccess = () => {
              clearTimeout(timeout);
              logger.info(`Proactively cleared database: ${db.name}`);
              resolve(true);
            };

            deleteReq.onerror = () => {
              clearTimeout(timeout);
              logger.warn(`Proactive cleanup error for ${db.name}:`, deleteReq.error);
              resolve(false);
            };

            deleteReq.onblocked = () => {
              clearTimeout(timeout);
              logger.warn(`Proactive cleanup blocked for ${db.name}`);
              resolve(false);
            };
          } catch (e) {
            clearTimeout(timeout);
            logger.warn(`Proactive cleanup exception for ${db.name}:`, e);
            resolve(false);
          }
        });
      });

      const results = await Promise.all(deletePromises);
      const successCount = results.filter(r => r).length;
      logger.info(`Proactive cleanup: cleared ${successCount}/${cryptoDatabases.length} crypto databases`);

    } catch (error) {
      logger.warn('Error during proactive crypto database cleanup:', error);
      // Continue anyway - the normal timeout mechanism will handle it
    }
  }

  /**
   * Initialize Matrix client with stored credentials
   */
  async initialize() {
    if (this.isInitialized && this.client) {
      return this.client;
    }

    try {
      const creds = credentialStore.readCredentials();
      if (!creds) {
        throw new Error('No Matrix credentials found');
      }

      await this.createClient(creds);
      
      this.isInitialized = true;
      logger.info('Matrix service initialized successfully');
      
      return this.client;
    } catch (error) {
      logger.error('Failed to initialize Matrix service:', error);
      throw error;
    }
  }

  /**
   * Get the current Matrix client instance
   * @returns {MatrixClient|null} The Matrix client or null if not initialized
   */
  getClient() {
    return this.client;
  }

  // Custom fetch attached to the post-login matrix-js-sdk client. Two jobs:
  //   1. Inject X-TIM-User-Agent (gemSpec_TI-M_Basis; without it the gematik
  //      SBG returns 403 M_FORBIDDEN before the request reaches Synapse).
  //   2. Backfill Authorization from localStorage when the SDK hasn't
  //      attached it yet (early-bootstrap call sites).
  // Override the SDK's XHR-based uploadContent to inject X-TIM-User-Agent
  // (gemSpec_TI-M_Basis; the SBG returns 403 M_FORBIDDEN without it). The
  // SDK takes the XHR branch in browsers and bypasses fetchFn, so customFetch
  // alone does not cover media uploads. See
  // node_modules/matrix-js-sdk/lib/http-api/index.js:48-150 (v41).
  _installUploadOverride() {
    if (!this.client || typeof this.client.uploadContent !== 'function') return;
    this.client._originalUploadContent = this.client.uploadContent.bind(this.client);
    this.client.uploadContent = (file, opts) => uploadMedia(this.client, file, opts);
  }

  customFetch = (url, options = {}) => {
    const headers = new Headers(options.headers || {});

    if (!headers.has('Authorization')) {
      try {
        // Raw (non-validating) read: early-bootstrap call sites may have only a
        // partial blob, and we still want to backfill Authorization from it.
        const creds = credentialStore.readCredentials();
        if (creds) {
          const { accessToken } = creds;
          if (accessToken) {
            headers.set('Authorization', `Bearer ${accessToken}`);
          } else {
            logger.warn('Custom fetch: No access token found on client, request will likely fail.');
          }
        }
      } catch (error) {
        logger.error('Custom fetch: Failed to retrieve access token from storage:', error);
      }
    }

    if (!headers.has('X-TIM-User-Agent')) {
      headers.set('X-TIM-User-Agent', buildTIMUserAgent());
    }

    return fetch(url, { ...options, headers });
  };

  /**
   * Create and configure Matrix client
   */
  async createClient(credentials) {
    logger.info('Creating Matrix client', {
      userId: credentials.userId,
      deviceId: credentials.deviceId,
      homeserver: credentials.homeserverUrl,
    });

    // Create a unique crypto store name to avoid conflicts
    const cryptoStoreName = `matrix-js-sdk:crypto:${credentials.userId}:${credentials.deviceId}`;
    logger.debug('Using crypto store name:', cryptoStoreName);

    // Check if we should force clear crypto stores (manual override)
    const forceClearStores = localStorage.getItem('matrixForceClearStores') === 'true';

    if (forceClearStores) {
      logger.info('Force clearing crypto stores (manual override set)');
      await this.clearAllMatrixStores();
      // Clear the flag after use
      localStorage.removeItem('matrixForceClearStores');
    }

    // Only clear crypto databases on FRESH logins, not session restores (page refresh)
    // Check if this is the same device reconnecting by looking for existing session marker
    const sessionMarkerKey = `matrix_session_${credentials.userId}_${credentials.deviceId}`;
    const existingSessionMarker = localStorage.getItem(sessionMarkerKey);
    // Treat both 'active' and 'initializing' as session restore (cleanup already started/completed)
    const isSessionRestore = existingSessionMarker === 'active' || existingSessionMarker === 'initializing';

    if (isSessionRestore) {
      logger.debug('Session restore detected - skipping crypto database cleanup to preserve keys');
    } else {
      // Fresh login - mark as initializing BEFORE cleanup to prevent re-cleanup on crash
      localStorage.setItem(sessionMarkerKey, 'initializing');
      // Proactively clear ALL crypto databases before creating client
      // This prevents crypto init from hanging on stale data after recovery key reset
      logger.info('Fresh login detected - clearing crypto databases');
      try {
        await this.clearAllCryptoDatabasesBeforeLogin();
      } catch (proactiveCleanupError) {
        logger.warn('Proactive cleanup failed, continuing with login:', proactiveCleanupError.message);
      }
    }

    // Mark as fully active after cleanup completes (will be used to detect session restore on next page load)
    localStorage.setItem(sessionMarkerKey, 'active');

    // Only try to reset device keys if explicitly needed
    const shouldResetDeviceKeys = localStorage.getItem('matrixResetDeviceKeys') === 'true';
    if (shouldResetDeviceKeys) {
      logger.info('Resetting device keys on server (manual override set)');
      await this.resetDeviceKeys();
      localStorage.removeItem('matrixResetDeviceKeys');
    }
    
    // For development: Option to disable crypto to avoid key upload issues
    const disableCrypto = process.env.NODE_ENV === 'development' && 
                         localStorage.getItem('matrixDisableCrypto') === 'true';
    
    if (disableCrypto) {
      logger.info('Crypto disabled for development (set matrixDisableCrypto=false in localStorage to re-enable)');
    }
    
    // NED-4397: capture THIS client's owning login nonce so a refresh-401 from a
    // SUPERSEDED client stamps the OLD session's nonce (letting the consumer guard
    // reject it) instead of the live/current nonce. createClient() runs after the
    // login bump for this session, so the value is this session's nonce. Resolved
    // on a microtask (matrixAuthService is cached); a refresh (network 401) always
    // fires much later, so the closure below reads a settled value. Undefined only
    // in the tiny pre-microtask window → _performTokenRefresh falls back to a live
    // read (safe: no re-login has happened that early).
    let ownerNonce;
    import('./matrixAuthService')
      .then((m) => { ownerNonce = m.default?.getLoginNonce?.(); })
      .catch(() => { /* leave undefined; refresh path falls back to a live read */ });

    const opts = {
      baseUrl: credentials.homeserverUrl,
      accessToken: credentials.accessToken,
      userId: credentials.userId,
      deviceId: credentials.deviceId,
      refreshToken: credentials.refreshToken,
      idBaseUrl: credentials.identityServerUrl,
      // NOTE: timelineSupport intentionally disabled — it degrades /sync performance
      // by maintaining timeline forks for every room, causing delayed message delivery,
      // topic sync, and typing indicators. Jump-to-event uses /context API instead.
      tokenRefreshFunction: async (refreshToken) => {
        try {
        logger.debug('tokenRefreshFunction: Requesting token refresh using Matrix SDK');
        const { default: matrixAuthService } = await import('./matrixAuthService');
        // Pass homeserverUrl + userId + deviceId from the credentials closure so
        // refresh works (and, if it must recreate a cleared blob, recreates a
        // STRUCTURALLY VALID one with userId/deviceId) even if localStorage was
        // cleared — preventing the userId-less "unknown userId" init defect.
        const tokenData = await matrixAuthService.refreshToken(refreshToken, credentials.homeserverUrl, {
          userId: credentials.userId,
          deviceId: credentials.deviceId,
          // NED-4397: this client's owning login nonce (captured above).
          ownerNonce,
        });

        if (!tokenData) { 
          const errorStr = 'Token refresh failed: No token data returned';
          logger.error(errorStr);
          throw new Error(errorStr);
        }
        logger.debug('tokenRefreshFunction: Token refreshed successfully:');
        return tokenData;
        } catch (error) {
          logger.error('tokenRefreshFunction: Token refresh function failed:', error);
          throw error;
        }
      },
      // Only include crypto store if crypto is enabled
      ...(disableCrypto ? {} : {
        cryptoStore: new IndexedDBCryptoStore(
          window.indexedDB, 
          cryptoStoreName,
        ),
      }),
      fetchFn: this.customFetch,
      cryptoCallbacks: {
          getSecretStorageKey: async (opts, name) => {
          logger.debug('TI-M: getSecretStorageKey called for secret:', name);
          logger.debug('TI-M: opts parameter:', JSON.stringify(opts, null, 2));

          // Handle different parameter formats
          // The SDK may call with ({ keys: {...} }, name) or just (keys, undefined)
          let keyInfos = opts?.keys || opts;
          let keyId = null;

          // If opts has a keys property, use that
          if (opts && typeof opts === 'object' && opts.keys) {
            keyInfos = opts.keys;
          }

          // Try to determine the key ID to use
          // IMPORTANT: 'name' parameter is often a secret name (like m.cross_signing.master)
          // NOT a key ID. The actual key IDs are in the keyInfos object
          if (keyInfos && typeof keyInfos === 'object') {
            // Try to get the default key ID from keyInfos
            const keyIds = Object.keys(keyInfos);
            logger.debug('TI-M: Available key IDs:', keyIds);

            if (keyIds.length === 1) {
              // Only one key available, use it
              keyId = keyIds[0];
            } else {
              // Multiple keys, try to find default
              try {
                const defaultKeyData = await this.client?.getAccountData?.('m.secret_storage.default_key');
                const defaultKeyId = defaultKeyData?.getContent?.()?.key;
                if (defaultKeyId && keyInfos[defaultKeyId]) {
                  keyId = defaultKeyId;
                } else {
                  // Use first available key
                  keyId = keyIds[0];
                }
              } catch {
                // Fallback to first key
                keyId = keyIds[0];
              }
            }
          }

          if (!keyId) {
            logger.debug('TI-M: Could not determine key ID - no keyInfos available');
            return null;
          }

          logger.debug('TI-M: Using key ID:', keyId);

          // First check if we have a temporary verification key, otherwise use ephemeral store
          const hasTempKey = !!this._temporaryVerificationKey;
          const hasEphemeralKey = recoveryEphemeralStore.hasKey();
          logger.debug('TI-M: hasTempKey:', hasTempKey, 'hasEphemeralKey:', hasEphemeralKey);

          // Use getKeyAsync to ensure session storage is properly loaded before returning
          const userKeyBytes = this._temporaryVerificationKey || await recoveryEphemeralStore.getKeyAsync();

          if (!userKeyBytes || userKeyBytes.length !== 32) {
            logger.debug('TI-M: No recovery key available - returning null');
            logger.debug('TI-M: userKeyBytes length:', userKeyBytes?.length);
            return null;
          }

          logger.debug('TI-M: Returning recovery key for secret:', name, 'keyId:', keyId);
          // CRITICAL: Return tuple [keyId, keyBytes] like Element Web does
          return [keyId, new Uint8Array(userKeyBytes)];
        },
      },
    };

    this.client = createClient(opts);
    this._installUploadOverride();

    if (!disableCrypto) {
      logger.info('Matrix client created, initializing Rust crypto');
      
      // Timeout for crypto initialization to prevent indefinite hangs
      // This can happen when there's stale crypto data from a previous session
      // (e.g., backup version mismatch after recovery key reset on another device)
      const CRYPTO_INIT_TIMEOUT = MatrixService.CRYPTO_INIT_TIMEOUT_MS;
      
      let cryptoInitTimeoutId;
      try {
        // Race between crypto init and timeout
        const initWithTimeout = Promise.race([
          this.client.initRustCrypto(),
          new Promise((_, reject) => {
            cryptoInitTimeoutId = setTimeout(() => reject(new Error('CRYPTO_INIT_TIMEOUT')), CRYPTO_INIT_TIMEOUT);
          }),
        ]);

        await initWithTimeout;
        clearTimeout(cryptoInitTimeoutId); // Clean up timeout on success
        logger.info('Rust crypto initialized successfully');
        await this._applyDeviceIsolationPolicy();
      } catch (cryptoError) {
        clearTimeout(cryptoInitTimeoutId); // Clean up timeout on error path
        logger.error('Crypto initialization failed:', cryptoError);

        // Only clear stores if it's a specific key conflict, account mismatch, or timeout
        // Timeout indicates stale crypto state (e.g., backup version mismatch after remote reset)
        if (
          cryptoError.message.includes('already exists') ||
          cryptoError.message.includes('One time key') ||
          cryptoError.message.includes('crypto_store_already_open') ||
          cryptoError.message.includes('account in the store doesn\'t match') ||
          cryptoError.message === 'CRYPTO_INIT_TIMEOUT'
        ) {
          // Log specific message for timeout case
          if (cryptoError.message === 'CRYPTO_INIT_TIMEOUT') {
            logger.warn('Crypto initialization timed out - likely stale crypto store from previous session');
          }
          logger.info('Key conflict detected, will clear stores and retry');

          // If it's specifically a one-time key conflict, try to reset server keys first
          if (cryptoError.message.includes('One time key')) {
            logger.info('One-time key conflict detected, attempting to reset server keys');
            const resetSuccess = await this.resetDeviceKeys();

            if (resetSuccess) {
              // If device was fully reset, we need to re-login
              logger.warn('Device was reset on server, re-login required');
              throw new Error('DEVICE_RESET_REQUIRED');
            }
          }

          // Stop the current client to release database connections before clearing stores
          if (this.client) {
            logger.debug('Stopping client to release crypto store connections');
            this.client.stopClient();
            // Wait for client to fully stop and release connections
            await new Promise(resolve => setTimeout(resolve, 300));
          }

          // Clear the conflicting stores
          await this.clearAllMatrixStores();

          // Wait a bit more after clearing
          await new Promise(resolve => setTimeout(resolve, 100));

          // Recreate client with fresh crypto store
          this.client = createClient(opts);
          this._installUploadOverride();

          // Try one more time with the same timeout protection
          let retryTimeoutId;
          try {
            const retryWithTimeout = Promise.race([
              this.client.initRustCrypto(),
              new Promise((_, reject) => {
                retryTimeoutId = setTimeout(() => reject(new Error('CRYPTO_INIT_TIMEOUT')), CRYPTO_INIT_TIMEOUT);
              }),
            ]);
            await retryWithTimeout;
            clearTimeout(retryTimeoutId); // Clean up timeout on success
            // Clear reload counter on success
            localStorage.removeItem('matrixCryptoReloadCount');
            logger.info('Rust crypto initialized successfully after store clear');
            await this._applyDeviceIsolationPolicy();
          } catch (retryError) {
            clearTimeout(retryTimeoutId); // Clean up timeout on error
            logger.error('Failed to initialize crypto after retry:', retryError);

            // If cleanup failed and we're still timing out, force a page reload
            // This is the only reliable way to terminate WASM IndexedDB connections
            if (retryError.message === 'CRYPTO_INIT_TIMEOUT') {
              // Check reload counter to prevent infinite reload loop
              const reloadCount = parseInt(localStorage.getItem('matrixCryptoReloadCount') || '0', 10);
              
              if (reloadCount >= 2) {
                logger.error('Max crypto reload attempts reached, forcing logout');
                localStorage.removeItem('matrixCryptoReloadCount');
                localStorage.removeItem('matrixForceClearStores');
                // Force logout to clear all state - crypto is required for this app
                const { default: matrixAuthService } = await import('./matrixAuthService');
                await matrixAuthService.logout();
                throw new Error('CRYPTO_INIT_FAILED_LOGOUT');
              }
              
              logger.warn(`Crypto init timed out twice - forcing page reload (attempt ${reloadCount + 1}/2)`);
              localStorage.setItem('matrixForceClearStores', 'true');
              localStorage.setItem('matrixCryptoReloadCount', String(reloadCount + 1));
              // Small delay to ensure log is visible
              await new Promise(resolve => setTimeout(resolve, 100));
              window.location.reload();
              throw new Error('RELOADING'); // Prevent further execution
            }

            // For non-timeout errors, continue without crypto
            logger.warn('Continuing without encryption support');
            this.client = createClient({ ...opts, cryptoStore: undefined });
            this._installUploadOverride();

            // Emit event for UI notification
            window.dispatchEvent(new CustomEvent('matrix-crypto-disabled', {
              detail: { error: retryError.message },
            }));
          }
        } else {
          // For other errors, don't clear stores - just throw
          throw cryptoError;
        }
      }
    } else {
      logger.info('Skipping crypto initialization (disabled for development)');
    }
    
    // Setup key backup after crypto is initialized
    logger.debug('TI-M: About to call setupKeyBackup()');
    await this.setupKeyBackup();
    logger.debug('TI-M: setupKeyBackup() returned');

    // Setup dehydration after key backup
    logger.debug('Dehydration: About to call setupDehydration()');
    await this.setupDehydration();
    logger.debug('Dehydration: setupDehydration() returned');

    logger.debug('Setting up event handlers');
    this.setupEventHandlers();
    logger.debug('Event handlers setup completed');
    
    // Note: setSyncPresence is no longer needed since we disabled presence in sync
    // Presence is now managed by Page Visibility API
    
    // Setup Page Visibility API for presence management
    this.setupPageVisibilityHandling();
    
    logger.debug('Matrix client created with options:', opts);
    return this.client;
  }

  /**
   * Restrict OUTGOING Megolm room-key sharing to verified (cross-signed) devices only.
   *
   * Compliance: A_25514 ("TI-M clients MUST ensure that key sharing between devices of the
   * same actor includes only verified devices; enrolled-but-unverified devices are excluded
   * from key sharing"). A_25514 is purely an OUTBOUND requirement — it governs which devices
   * RECEIVE room keys, not whether incoming messages are decrypted.
   *
   * TWO-LAYER DESIGN (read before changing):
   *  (1) OUTBOUND — this method sets OnlySignedDevicesIsolationMode. The rust-crypto default
   *      is AllDevicesIsolationMode(false) (distribute keys to ALL devices regardless of
   *      verification). OnlySignedDevicesIsolationMode routes outbound sharing through
   *      `identityBasedStrategy` (RoomEncryptor.js) — keys are shared only to devices signed
   *      by their OWN owner's cross-signing identity. This is TOFU-compatible (does not require
   *      we have interactively SAS-verified the other user) and is exactly what A_25514 needs.
   *  (2) INBOUND — OnlySignedDevicesIsolationMode is a SINGLE setting the SDK consumes in BOTH
   *      encrypt AND decrypt paths. Its inbound half rejects any session whose sender device is
   *      not cross-signed (DecryptionFailureCode UNSIGNED_SENDER_DEVICE), which is NOT required
   *      by A_25514 and BREAKS dehydration message recovery (keys collected by a dehydrated
   *      device are rehydrated with untrusted sender_data and would be rejected). A VENDORED
   *      matrix-js-sdk patch — `patches/matrix-js-sdk+41.0.0.patch`, applied via patch-package
   *      postinstall — decouples this by mapping OnlySignedDevicesIsolationMode → Untrusted at
   *      decrypt time (rust-crypto.js `attemptEventDecryption`), so inbound decryption stays
   *      permissive. Net: cross-signed-only OUTBOUND + permissive INBOUND. The
   *      MessageBubble 'unableToDecryptUnverifiedSender' branch is now a defensive fallback
   *      (it only fires if that patch is ever lost).
   *
   * RE-VALIDATE on any matrix-js-sdk bump off 41.0.0: confirm both the OnlySignedDevicesIsolationMode
   * outbound strategy and the vendored decrypt-trust patch still hold (the patch must re-apply).
   *
   * INVARIANT: this is an in-memory, per-crypto-instance setting — it is NOT persisted and
   * resets to the permissive default on every initRustCrypto(). It MUST be re-applied after
   * EVERY successful crypto init; any new crypto-init path must call this method.
   */
  async _applyDeviceIsolationPolicy() {
    try {
      const crypto = this.client?.getCrypto();
      if (!crypto?.setDeviceIsolationMode) {
        logger.warn('A_25514: crypto.setDeviceIsolationMode unavailable; cannot restrict key sharing to verified devices');
        return;
      }

      // Re-evaluate the policy if our OWN identity later becomes verified (e.g. the user enters
      // their recovery key). Idempotent; the listener is removed in cleanup().
      this._registerIsolationReArm();

      // SENDER GATE (A_25514 send-block fix) — OnlySignedDevicesIsolationMode drives rust-crypto's
      // `identityBasedStrategy`, which ABORTS the ENTIRE outbound send (before it even looks at the
      // recipients) whenever OUR OWN cross-signing identity is not verified by this device:
      //   own identity None            -> CrossSigningNotSetup
      //   own identity !is_verified()  -> SendingFromUnverifiedDevice
      // That is precisely the state of a freshly logged-in skip-recovery user and of a rehydrated
      // dehydrated device, so arming the mode then makes them UNABLE TO SEND (messages stick on
      // retry until they enter their recovery key — the gematik test suite scenario UC12X.01.01
      // (TIM_V2_Basis_AF_12X0101) device "C" hit exactly this).
      // `getUserVerificationStatus(self).isCrossSigningVerified()` is the exact JS mirror of the rust
      // `OwnUserIdentity::is_verified()` gate (rust-crypto.js getUserVerificationStatus wraps
      // UserIdentity.isVerified()), so we arm the compliance restriction ONLY once it is true.
      // Until then we use the SDK default (AllDevicesIsolationMode) so the user can send — this
      // permissive window is forced by the SDK abort above, not a relaxation of the key-sharing
      // restriction — and the UserTrustStatusChanged re-arm upgrades to
      // OnlySignedDevicesIsolationMode the instant we verify. (Device-level signedByOwner is NOT used
      // here: per the SDK it "doesn't necessarily mean we have verified the device", so it can be
      // true while the own identity is still unverified — gating on it would re-introduce the abort.)
      let ownIdentityVerified = false;
      try {
        const userId = this.client.getUserId?.();
        const status = userId ? await crypto.getUserVerificationStatus(userId) : null;
        ownIdentityVerified = !!status?.isCrossSigningVerified?.();
      } catch (statusError) {
        logger.warn('A_25514: own-identity verification check failed; treating as unverified:', statusError?.message || statusError);
      }

      if (ownIdentityVerified) {
        crypto.setDeviceIsolationMode(new OnlySignedDevicesIsolationMode());
        logger.info('A_25514: own identity verified -> OnlySignedDevicesIsolationMode (keys shared to verified devices only)');
      } else {
        crypto.setDeviceIsolationMode(new AllDevicesIsolationMode(false));
        logger.info('A_25514: own identity not yet verified -> AllDevicesIsolationMode (permit send until cross-signing completes; strict mode would abort all sends from an un-cross-signed sender)');
      }
    } catch (error) {
      logger.error('A_25514: failed to apply device isolation policy:', error);
    }
  }

  /**
   * Whether an account-data event type carries cross-signing / secret-storage / key-backup secrets
   * (the signals that can change our own-identity verification, e.g. via secret gossip). Shared by
   * the dehydration trigger and the device-isolation re-arm so the two stay in sync.
   * @private
   */
  _isCrossSigningSecretAccountData(type) {
    return !!type && (
      type.includes('m.cross_signing') ||
      type.includes('m.secret_storage') ||
      type.includes('m.megolm_backup')
    );
  }

  /**
   * Register a one-time listener that re-applies the device isolation policy whenever OUR OWN
   * cross-signing identity verification changes (e.g. after the user enters their recovery key and
   * the device cross-signs itself). This upgrades the in-memory isolation mode from the permissive
   * AllDevicesIsolationMode back to the A_25514-compliant OnlySignedDevicesIsolationMode the moment
   * we become verified, so the stuck first message sends on its next retry. Idempotent; torn down in
   * cleanup() alongside the other crypto listeners.
   * @private
   */
  _registerIsolationReArm() {
    if (typeof this.client?.on !== 'function') {
      return;
    }
    // Already registered on the CURRENT client — nothing to do. Guarding on the BOUND client (not
    // merely on the handler's existence) is load-bearing: the crypto-init store-clear retry path and
    // re-login swap `this.client`, and the listener must follow to the new active client — otherwise
    // it stays attached to the discarded client and the re-arm goes dead (A_25514 never re-arms after
    // the user verifies, silently defeating the compliance restriction for the rest of the session).
    if (this._isolationReArmHandler && this._isolationReArmClient === this.client) {
      return;
    }
    // Moving to a new client: detach the listeners from the previous one if it is still around.
    if (this._isolationReArmHandler) {
      this._detachIsolationReArm(this._isolationReArmClient);
    }
    if (!this._isolationReArmHandler) {
      // UserTrustStatusChanged passes (userId) — react only to OUR OWN identity. setDeviceIsolationMode
      // does not emit it, so re-applying cannot loop. The returned promise is ignored by the emitter
      // but lets tests await the re-arm.
      this._isolationReArmHandler = (changedUserId) => {
        if (changedUserId !== this.client?.getUserId?.()) {
          return undefined;
        }
        return this._applyDeviceIsolationPolicy();
      };
      // Defence-in-depth: the own identity can also become verified via secret GOSSIP (private
      // cross-signing keys arriving from another device). The primary UserTrustStatusChanged listener
      // is expected to cover that, but to be robust against any verification path that doesn't surface
      // it, also re-evaluate on key-backup status changes and on cross-signing / secret-storage /
      // key-backup account-data. All idempotent; none are emitted by setDeviceIsolationMode /
      // getUserVerificationStatus, so they cannot loop.
      this._isolationReArmReevalHandler = () => this._applyDeviceIsolationPolicy();
      this._isolationReArmAccountDataHandler = (event) => {
        if (this._isCrossSigningSecretAccountData(event?.getType?.())) {
          return this._applyDeviceIsolationPolicy();
        }
        return undefined;
      };
    }
    this.client.on(CryptoEvent.UserTrustStatusChanged, this._isolationReArmHandler);
    this.client.on(CryptoEvent.KeyBackupStatus, this._isolationReArmReevalHandler);
    this.client.on(ClientEvent.AccountData, this._isolationReArmAccountDataHandler);
    this._isolationReArmClient = this.client;
    logger.debug('A_25514: registered device-isolation re-arm listeners (trust / key-backup / account-data)');
  }

  /**
   * Detach all A_25514 device-isolation re-arm listeners from the given client.
   * @private
   */
  _detachIsolationReArm(client) {
    if (typeof client?.off !== 'function') {
      return;
    }
    if (this._isolationReArmHandler) {
      client.off(CryptoEvent.UserTrustStatusChanged, this._isolationReArmHandler);
    }
    if (this._isolationReArmReevalHandler) {
      client.off(CryptoEvent.KeyBackupStatus, this._isolationReArmReevalHandler);
    }
    if (this._isolationReArmAccountDataHandler) {
      client.off(ClientEvent.AccountData, this._isolationReArmAccountDataHandler);
    }
  }

  /**
   * Setup key backup and restore encrypted messages
   */
  async setupKeyBackup() {
    logger.debug('TI-M: setupKeyBackup() called');
    try {
      const crypto = this.client.getCrypto();
      logger.debug('TI-M: Got crypto instance:', !!crypto);
      if (!crypto) {
        logger.debug('TI-M: Crypto not available - exiting');
        return;
      }

      // Use the correct Rust crypto API to check for existing backup
      logger.debug('TI-M: Calling getKeyBackupInfo()...');
      const backupInfo = await crypto.getKeyBackupInfo();
      logger.debug('TI-M: backupInfo result:', backupInfo);
      if (!backupInfo) {
        logger.info('No key backup found on server');
        logger.debug('TI-M: No backup found, trying checkKeyBackupAndEnable()');
        // Try to check and enable backup anyway
        const backupCheck = await crypto.checkKeyBackupAndEnable();
        logger.debug('TI-M: checkKeyBackupAndEnable result:', backupCheck);
        if (backupCheck) {
          logger.info('Key backup enabled after check:', backupCheck);
        }
        return;
      }

      logger.debug('TI-M: Key backup found! Version:', backupInfo.version);
      
      // Check if backup is already trusted
      logger.debug('TI-M: Checking if backup is trusted...');
      const trustInfo = await crypto.isKeyBackupTrusted(backupInfo);
      logger.info('Key backup trust info:', trustInfo);
      logger.debug('TI-M: Trust info:', trustInfo);
      
      // Log more details about the backup
      logger.debug('TI-M: Backup auth_data:', backupInfo.auth_data);
      logger.debug('TI-M: Current device ID:', this.client.getDeviceId());
      logger.debug('TI-M: Current user ID:', this.client.getUserId());
      
      // Check device verification status
      const deviceId = this.client.getDeviceId();
      const userId = this.client.getUserId();
      logger.debug('TI-M: Checking device verification status...');
      const device = await crypto.getUserDeviceInfo([userId]);
      const myDevice = device.get(userId)?.get(deviceId);
      logger.debug('TI-M: My device verified status:', myDevice?.verified);
      logger.debug('TI-M: My device trust level:', myDevice?.trustLevel);
      
      // Check cross-signing status
      const crossSigningStatus = await crypto.getCrossSigningStatus();
      logger.debug('TI-M: Cross-signing status:', crossSigningStatus);
      
      if (!trustInfo.trusted) {
        logger.debug('TI-M: Backup NOT trusted, attempting to restore...');
        
        // Try to bootstrap cross-signing if not set up
        if (!crossSigningStatus.privateKeysInSecretStorage || !crossSigningStatus.publicKeysOnDevice) {
          logger.debug('TI-M: Cross-signing not fully set up, attempting to bootstrap...');
          try {
            logger.debug('TI-M: Bootstrapping cross-signing with recovery key...');
            await crypto.bootstrapCrossSigning({
              authUploadDeviceSigningKeys: async (makeRequest) => {
                logger.debug('TI-M: Uploading device signing keys...');
                await makeRequest({});
              },
            });
            logger.debug('TI-M: ✅ CROSS-SIGNING BOOTSTRAPPED SUCCESSFULLY');

            // Mark our own device as verified
            logger.debug('TI-M: MARKING OWN DEVICE AS VERIFIED...');
            await crypto.setDeviceVerified(userId, deviceId);
            logger.debug('TI-M: ✅ DEVICE MARKED AS VERIFIED');
          } catch (bootstrapErr) {
            logger.debug('TI-M: Failed to bootstrap cross-signing:', bootstrapErr.message);
          }
        } else {
          // Cross-signing is set up, but we still need to verify the device
          logger.debug('TI-M: Cross-signing already set up, checking device verification...');
          const device = await crypto.getUserDeviceInfo([userId]);
          const myDevice = device.get(userId)?.get(deviceId);
          
          if (myDevice?.verified !== 1 && myDevice?.verified !== true) {
            logger.debug('TI-M: Device not verified despite cross-signing being set up');
            logger.debug('TI-M: ATTEMPTING TO VERIFY DEVICE...');
            try {
              await crypto.setDeviceVerified(userId, deviceId);
              logger.debug('TI-M: ✅ DEVICE HAS BEEN VERIFIED SUCCESSFULLY');
            } catch (verifyErr) {
              logger.debug('TI-M: Failed to verify device:', verifyErr.message);
            }
          } else {
            logger.debug('TI-M: Device already verified (status:', myDevice?.verified, ')');
            // Even if device shows as verified, try to re-verify it
            logger.debug('TI-M: RE-VERIFYING DEVICE TO ENSURE BACKUP TRUST...');
            try {
              await crypto.setDeviceVerified(userId, deviceId);
              logger.debug('TI-M: ✅ DEVICE RE-VERIFIED SUCCESSFULLY');
            } catch (verifyErr) {
              logger.debug('TI-M: Re-verification error (may be already verified):', verifyErr.message);
            }
          }
        }
        
        try {
          // First check if we already have a stored backup key
          logger.debug('TI-M: Checking for stored backup key...');
          const storedKey = await crypto.getSessionBackupPrivateKey();
          logger.debug('TI-M: Stored key exists:', !!storedKey);

          // Only attempt automatic key injection if explicitly enabled
          const { isEnabled } = await import('../config/featureFlags');
          const autoRecoveryEnabled = isEnabled('autoRecovery');

          if (!trustInfo.trusted && autoRecoveryEnabled) {
            logger.debug('TI-M: AutoRecovery enabled — waiting for user-provided recovery key');
            // AutoRecovery feature flag is enabled but requires user to provide their own recovery key
            // This flag only controls whether the recovery flow automatically starts, not key injection
          } else if (!trustInfo.trusted) {
            logger.debug('TI-M: AutoRecovery disabled — waiting for user-provided key');
          }
          
          // Try loading from secret storage as an alternative approach
          try {
            logger.debug('TI-M: Attempting to load backup key from secret storage...');
            await crypto.loadSessionBackupPrivateKeyFromSecretStorage();
            logger.debug('TI-M: Successfully loaded from secret storage');
          } catch (secretErr) {
            logger.debug('TI-M: Could not load from secret storage:', secretErr.message);
          }
          
          // Check if device is now verified
          const deviceAfterSetup = await crypto.getUserDeviceInfo([userId]);
          const myDeviceAfter = deviceAfterSetup.get(userId)?.get(deviceId);
          logger.debug('TI-M: Device verified after setup:', myDeviceAfter?.verified);
          
          // Force a re-check to enable backup now that we have the key
          logger.debug('TI-M: Rechecking backup trust after storing key...');
          const recheckResult = await crypto.checkKeyBackupAndEnable();
          logger.debug('TI-M: Recheck result:', recheckResult);
          if (recheckResult && recheckResult.trustInfo && recheckResult.trustInfo.trusted) {
            logger.debug('TI-M: ✅ BACKUP IS NOW TRUSTED! STARTING RESTORATION...');
            
            // Start restoring from backup
            logger.info('Starting key backup restoration...');
            logger.debug('TI-M: CALLING RESTOREKEYBACKUP()...');
            await crypto.restoreKeyBackup();
            logger.info('Key backup restoration completed');
            logger.debug('TI-M: 🎉 KEY BACKUP RESTORATION COMPLETED SUCCESSFULLY!');
          } else {
            logger.debug('TI-M: Backup still NOT trusted after providing key');
            logger.debug('TI-M: Trust info details:', recheckResult?.trustInfo);
            
            // Try one more approach - attempt to restore anyway even if not trusted
            logger.debug('TI-M: Attempting to verify key matches backup...');
            const currentStoredKey = await crypto.getSessionBackupPrivateKey();
            if (currentStoredKey) {
              logger.debug('TI-M: Key is stored, but backup not trusting it');
              logger.debug('TI-M: Attempting to restore anyway with untrusted backup...');
              
              try {
                // Try to restore even though it's not trusted
                // This might work if the key is correct but trust verification has issues
                logger.debug('TI-M: Force attempting restoreKeyBackup despite trust issues...');
                await crypto.restoreKeyBackup();
                logger.debug('TI-M: 🎉 RESTORE SUCCEEDED DESPITE TRUST ISSUES!');
              } catch (restoreErr) {
                logger.debug('TI-M: Force restore failed:', restoreErr.message);
                
                // Try using the active session backup version
                const activeVersion = await crypto.getActiveSessionBackupVersion();
                logger.debug('TI-M: Active backup version:', activeVersion);
                
                if (!activeVersion && backupInfo.version) {
                  logger.debug('TI-M: No active backup. The recovery key does not match the server backup.');
                  logger.debug('TI-M: Options:');
                  logger.debug('TI-M: 1. Get the correct recovery key for this backup');
                  logger.debug('TI-M: 2. Delete this backup and create a new one (will lose old messages)');
                  logger.debug('TI-M: 3. Continue without backup (messages will decrypt as keys arrive)');
                  
                  // For now, continue without backup restoration
                  logger.debug('TI-M: Continuing without backup restoration');
                  logger.debug('TI-M: Messages will decrypt as keys are shared from other devices');
                }
              }
            }
          }
        } catch (restoreError) {
          logger.error('Failed to restore key backup:', restoreError);
          logger.debug('TI-M: ERROR restoring backup:', restoreError.message);
        }
      } else {
        logger.info('Key backup is already trusted');
        logger.debug('TI-M: Backup is ALREADY TRUSTED');
        
        // Ensure backup is enabled
        logger.debug('TI-M: Checking active backup version...');
        const activeVersion = await crypto.getActiveSessionBackupVersion();
        logger.debug('TI-M: Active version:', activeVersion, 'Current version:', backupInfo.version);
        if (activeVersion !== backupInfo.version) {
          logger.debug('TI-M: Versions differ, enabling backup...');
          const enableResult = await crypto.checkKeyBackupAndEnable();
          logger.info('Key backup enabled:', enableResult);
          logger.debug('TI-M: Backup enabled:', enableResult);
        } else {
          logger.info('Key backup already active for version:', activeVersion);
          logger.debug('TI-M: Backup already active');
        }
        
        // Restore keys from backup if not already done
        try {
          logger.info('Restoring keys from trusted backup...');
          logger.debug('TI-M: Restoring keys from trusted backup...');
          await crypto.restoreKeyBackup();
          logger.info('Key restoration completed');
          logger.debug('TI-M: 🎉 KEY RESTORATION COMPLETED!');
        } catch (restoreErr) {
          logger.warn('Key restoration error (may be already complete):', restoreErr.message);
          logger.debug('TI-M: Restore error (may be already complete):', restoreErr.message);
        }
      }
      
      // ✅ Trigger dehydration check after key backup setup completes
      // This ensures dehydration runs after cross-signing and backup are ready
      logger.debug('TI-M: Key backup setup complete, triggering dehydration check...');
      // Store timer ID for cleanup if client is destroyed before timer fires
      this._dehydrationBackupTimer = setTimeout(async () => {
        this._dehydrationBackupTimer = null;
        // Guard against destroyed client
        if (!this.client) {
          logger.debug('TI-M: Skipping dehydration check - client destroyed');
          return;
        }
        try {
          await this.triggerDehydrationCheck();
          logger.info('TI-M: Dehydration check triggered after key backup setup');
        } catch (error) {
          logger.warn('TI-M: Dehydration check failed after key backup:', error.message);
        }
      }, 1000); // 1 second delay to ensure everything is settled
      
    } catch (error) {
      logger.error('Failed to setup key backup:', error);
      logger.debug('TI-M: CRITICAL ERROR in setupKeyBackup:', error.message, error.stack);
      // Don't throw - allow app to continue even if backup fails
    }
    logger.debug('TI-M: setupKeyBackup() finished');
  }

  /**
   * Set up event handlers for the Matrix client
   */
  setupEventHandlers() {
    if (!this.client) return;

    logger.debug('Setting up Matrix event handlers');
    // Dehydration event handlers
    this.setupDehydrationEventHandlers();

    // Setup session invalidation detection
    this.setupSessionInvalidationHandler();

    // Sync state handler
    this.client.on(ClientEvent.Sync, (state, prevState, data) => {
      // DEBUG: Inspect sync response for account_data
      if (data?.accountData) {
        logger.debug('[MatrixService] Sync response contains account_data:', data.accountData.length, 'events');
        data.accountData.forEach(event => {
          logger.debug('[MatrixService] Account data event type:', event.type);
          if (event.type === 'm.direct') {
            logger.debug('[MatrixService] m.direct found in sync response! Content:', JSON.stringify(event.content));
          }
        });
      } else {
        logger.debug('[MatrixService] Sync response has NO account_data field');
      }

      this.syncState = state;
      logger.debug('Sync state change:', {
        newState: state,
        prevState: prevState,
        isSyncing: this.isSyncing,
      });

      if (state === 'PREPARED') {
        this.isSyncing = true;
        logger.info('Matrix sync prepared - real-time events active');
        // E2E test marker - used by Playwright to detect sync completion
        logger.info('[E2E_SYNC_READY]');

        logger.debug('Matrix sync prepared - real-time events active');

        this.onSyncPrepared();
      } else if (state === 'SYNCING') {
        logger.debug('Matrix client is syncing');
        this.isSyncing = true;
      } else if (state === 'CATCHUP') {
        logger.debug('Matrix client catching up on missed events');
      } else if (state === 'ERROR') {
        this.isSyncing = false;
        logger.error('Matrix sync error:', data);

        // Handle crypto store errors specifically
        if (data?.error?.message?.includes('crypto store') ||
            data?.error?.message?.includes('InvalidStateError') ||
            data?.error?.message?.includes('IDBDatabase')) {
          logger.error('Crypto store error detected during sync:', data.error);
          // Log for debugging - hooks will handle state updates from client events
        }
      } else if (state === 'STOPPED') {
        this.isSyncing = false;
        this.isSyncStarted = false; // Reset on stop
        logger.info('Matrix sync stopped');
      } else if (state === 'RECONNECTING') {
        logger.debug('Matrix sync reconnecting');
      }

      // Sync state is now handled by hooks listening to ClientEvent.Sync directly
    });

    // Room events - log only for debugging
    this.client.on(ClientEvent.Room, (room) => {
      logger.debug('Room event:', room.roomId, room.name);
      // Hooks will handle room updates by listening to this event directly
    });

    // Timeline events are handled by hooks directly

    // Handle crypto errors to prevent key upload conflicts
    logger.debug('Setting up crypto error handlers');
    this.client.on('crypto.keyBackupFailed', (error) => {
      logger.warn('Key backup failed:', error.message);
      // Don't throw - just log and continue
    });
    
    this.client.on('crypto.keyBackupRestoreFailed', (error) => {
      logger.warn('Key backup restore failed:', error.message);
      // Don't throw - just log and continue
    });

    // Setup verification request listener
    this.setupVerificationListener();

    // Listen for m.direct account data updates to clear cache when confirmed
    this.client.on(ClientEvent.AccountData, (event) => {
      if (event.getType() === 'm.direct') {
        const content = event.getContent();
        const confirmedRoomIds = new Set();

        // Collect all room IDs from m.direct
        Object.values(content).forEach(roomList => {
          if (Array.isArray(roomList)) {
            roomList.forEach(roomId => confirmedRoomIds.add(roomId));
          }
        });

        // Remove from pending cache if now confirmed in m.direct
        for (const [roomId, metadata] of this.pendingDMRooms.entries()) {
          if (confirmedRoomIds.has(roomId)) {
            // Clear the timer
            if (metadata.timerId) {
              clearTimeout(metadata.timerId);
            }
            this.pendingDMRooms.delete(roomId);
            logger.debug(`[matrixService] Removed ${roomId} from pending cache (confirmed in m.direct event)`);
          }
        }
      }
    });

    // Handle HTTP errors that might include key upload failures
    this.client.on('httpError', (error, method, url) => {
      if (url.includes('/keys/upload') && error.httpStatus === 400) {
        logger.warn('Key upload failed (400 Bad Request) - duplicate keys on server:', error.message);

        // Check if it's the one-time key conflict
        if (error.message?.includes('One time key') || error.message?.includes('already exists')) {
          logger.error('One-time key conflict detected. Server has these keys already.');

          // Set a flag for user to manually reset if needed
          localStorage.setItem('matrixKeyConflictDetected', 'true');

          // Log the conflict - components can check localStorage flag if needed
          logger.error('Key upload conflict:', {
            error: error.message,
            suggestion: 'Try logging out and back in, or reset device keys',
          });
        }
        // Don't throw - Matrix SDK will handle retries
      } else if (url.includes('/sync')) {
        logger.error('Sync HTTP Error:', {
          method,
          url,
          status: error.httpStatus,
          message: error.message,
        });
      } else if (error.httpStatus >= 400) {
        logger.error('HTTP Error:', {
          method,
          url,
          status: error.httpStatus,
          message: error.message,
        });
      }
    });

    // Real-time message handling - use generic event listener since RoomTimeline doesn't work reliably
    this.client.on('event', (event) => {
      const eventType = event.getType();
      const roomId = event.getRoomId();
      const sender = event.getSender();
      const currentUserId = this.client.getUserId();
      
      // Log important events for debugging
      if (roomId && ['m.room.message', 'm.room.encrypted', 'm.room.redaction', 'm.room.member'].includes(eventType)) {
        logger.debug('Matrix event received:', {
          type: eventType,
          roomId: roomId,
          sender: sender,
        });
      }
      
      // Handle room messages for real-time updates
      if (eventType === 'm.room.message' && roomId) {
        logger.debug('Real-time message received:', {
          eventType,
          roomId,
          sender,
          isFromCurrentUser: sender === currentUserId,
        });
        
        // Hooks will handle message updates by listening to client events directly
      }
      
      // Handle encrypted messages
      if (eventType === 'm.room.encrypted' && roomId) {
        logger.debug('Real-time encrypted message received:', {
          eventType,
          roomId,
          sender,
        });
        
        // Hooks will handle encrypted message updates by listening to client events
      }
      
      // Handle message redactions (deletions)
      if (eventType === 'm.room.redaction' && roomId) {
        // Get redacted event ID from event content or properties
        let redactsEventId = null;
        try {
          // Try different ways to get the redacted event ID
          redactsEventId = event.redacts || 
                          event.event?.redacts || 
                          event.getContent()?.redacts ||
                          (event.event && event.event.redacts);
        } catch (e) {
          logger.debug('Could not get redacts ID:', e);
        }
        
        logger.debug('Real-time message redaction received:', {
          eventType,
          roomId,
          redactsEventId,
        });
        
        // Hooks will handle redaction updates by listening to client events
      }
      
      // Handle message edits/replacements
      if (eventType === 'm.room.message' && roomId) {
        const content = event.getContent();
        const isEdit = content['m.relates_to']?.rel_type === 'm.replace';
        
        if (isEdit) {
          const originalEventId = content['m.relates_to']?.event_id;
          
          logger.debug('Real-time message edit received:', {
            eventType,
            roomId,
            sender,
            originalEventId,
            newContent: content['m.new_content']?.body,
          });
          
          // Hooks will handle message edit updates by listening to client events
        }
      }
      
      // Handle read receipts  
      if (eventType === 'm.receipt' && roomId) {
        const receipts = event.getContent();
        
        logger.debug('Real-time read receipt received:', {
          eventType,
          roomId,
          receipts,
        });
        
        // Hooks will handle read receipt updates
      }
      
      // Handle room membership changes (joins, leaves, kicks, bans)
      if (eventType === 'm.room.member' && roomId) {
        const membership = event.getContent()?.membership;
        const targetUserId = event.getStateKey();

        logger.debug('Real-time membership change received:', {
          eventType,
          roomId,
          sender,
          targetUserId,
          membership,
        });

        // Hooks will handle membership updates
      }
      
      // Handle room avatar changes
      if (eventType === 'm.room.avatar' && roomId) {
        const newAvatar = event.getContent()?.url;
        
        logger.debug('Real-time room avatar change received:', {
          eventType,
          roomId,
          sender,
          newAvatar,
        });
        
        // Hooks will handle room avatar updates
      }
    });

    // Room state events (includes room deletion, kicks, bans, etc.)
    this.client.on(ClientEvent.RoomStateEvent, (event, state) => {
      logger.debug('Room state event:', event.getType(), 'in room:', state.roomId);
      
      // Handle events that might affect room membership or existence
      if (event.getType() === 'm.room.member' || 
          event.getType() === 'm.room.tombstone' ||
          event.getType() === 'm.room.create') {
        logger.debug('Room membership/existence changed');
        // Hooks will detect room state changes
      }
    });

    // Room member events (kicks, bans, leaves)
    this.client.on(ClientEvent.RoomMember, (event, state, member) => {
      // Check if this affects the current user
      const currentUserId = this.client.getUserId();
      if (member.userId === currentUserId) {
        logger.debug('Current user membership changed:', member.membership, 'in room:', member.roomId);
        // Hooks will detect membership changes
      }
    });

    // Redaction events (message deletions) - more specific handler
    this.client.on(ClientEvent.RoomRedaction, (event, room) => {
      logger.debug('Message redaction event:', {
        eventId: event.getId(),
        redactsEventId: event.getRedacts(),
        sender: event.getSender(),
        roomId: room.roomId,
      });
      
      // Hooks will handle redaction events
    });

    // Room receipt events (read receipts)
    this.client.on(ClientEvent.RoomReceipt, (event, room) => {
      logger.debug('Read receipt event in room:', room.roomId);
      // Hooks will handle read receipt events
    });

    // Message decryption events - refresh UI when messages are decrypted
    // This is crucial after recovery key restoration to show decrypted messages
    this.client.on(MatrixEventEvent.Decrypted, (event, err) => {
      if (err) {
        logger.debug('Message decryption failed:', event.getId(), err.message);
        return;
      }

      logger.debug('Message decrypted successfully:', event.getId());
      // Hooks will detect decrypted messages
    });
  }

  /**
   * Setup event handlers for dehydration lifecycle events
   * Provides logging and monitoring for dehydration operations
   * TODO - Remove once dehydration works reliably
   */
  setupDehydrationEventHandlers() {
    if (!this.client) return;

    logger.debug('Setting up dehydration event handlers');

    // Key cached in local storage
    this.client.on(CryptoEvent.DehydrationKeyCached, () => {
      logger.info('Dehydration: Key has been cached locally');
    });

    // New dehydrated device created
    this.client.on(CryptoEvent.DehydratedDeviceCreated, () => {
      logger.info('Dehydration: New dehydrated device created');
    });

    // Device uploaded to server
    this.client.on(CryptoEvent.DehydratedDeviceUploaded, () => {
      logger.info('Dehydration: Device uploaded to server successfully');
    });

    // Rehydration started
    this.client.on(CryptoEvent.RehydrationStarted, () => {
      logger.info('Dehydration: Rehydration started - restoring message history');
    });

    // Rehydration progress
    this.client.on(CryptoEvent.RehydrationProgress, (roomKeys, toDeviceMessages) => {
      logger.debug(`Dehydration: Rehydration progress - ${roomKeys} room keys, ${toDeviceMessages} messages`);
    });

    // Rehydration completed
    this.client.on(CryptoEvent.RehydrationCompleted, () => {
      logger.info('Dehydration: Rehydration completed successfully');
    });

    // Rehydration error (non-fatal)
    this.client.on(CryptoEvent.RehydrationError, (errorMessage) => {
      logger.warn('Dehydration: Rehydration failed:', errorMessage);
      // SDK will continue and create new device, so this is non-fatal
    });

    // Device rotation error
    this.client.on(CryptoEvent.DehydratedDeviceRotationError, (errorMessage) => {
      logger.error('Dehydration: Device rotation failed:', errorMessage);
      // Log for monitoring - rotation will retry next cycle
    });

    logger.debug('Dehydration event handlers registered');
  }

  /**
   * Read alwaysAppearOffline setting from Matrix account data
   * Used during initialization before React context is available
   * @returns {boolean} Whether to always appear offline
   */
  getAlwaysAppearOfflineFromStorage() {
    try {
      if (!this.client) {
        return true; // Default per TI requirements if client not ready
      }
      
      const accountDataEvent = this.client.getAccountData('kPrivacySettingsKey');
      if (accountDataEvent) {
        const settings = accountDataEvent.getContent();
        return !(settings.OnlinePresenceVisibility ?? false);
      }
    } catch (error) {
      logger.warn('Failed to read alwaysAppearOffline from Matrix account data:', error);
    }
    return true; // Default per TI requirements
  }

  /**
   * Setup Page Visibility API to manage presence based on tab visibility
   * Sets presence to 'online' when tab is visible, 'unavailable' when hidden
   */
  setupPageVisibilityHandling() {
    if (typeof document === 'undefined' || !document.addEventListener) {
      logger.warn('Page Visibility API not available (SSR or old browser)');
      return;
    }

    // Initialize sendPresenceUpdates based on stored alwaysAppearOffline setting
    // This ensures correct behavior before React SettingsContext can run
    const alwaysAppearOffline = this.getAlwaysAppearOfflineFromStorage();
    if (alwaysAppearOffline) {
      this.sendPresenceUpdates = false;
      logger.debug('Initialized sendPresenceUpdates=false based on alwaysAppearOffline setting');
    }

    // Remove existing listener before adding new one to prevent duplicates
    // This is critical when createClient() is called multiple times (e.g., during crypto retries)
    if (this._visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this._visibilityChangeHandler);
      this._visibilityChangeHandler = null;
      logger.debug('Removed existing visibility change listener before re-registering');
    }

    // Handler for visibility changes
    const handleVisibilityChange = async () => {
      if (!this.client || !this.sendPresenceUpdates) return;

      const newPresence = document.hidden ? 'unavailable' : 'online';
      const userId = this.client.getUserId();

      // Throttle check to prevent 429 rate limit errors
      const now = Date.now();
      const timeSinceLastUpdate = now - this.lastPresenceUpdate;
      
      if (timeSinceLastUpdate < this.minPresenceInterval) {
        const timeUntilNext = this.minPresenceInterval - timeSinceLastUpdate;
        
        // Clear any existing pending timer
        if (this.pendingPresenceTimer) {
          clearTimeout(this.pendingPresenceTimer);
        }
        
        // Schedule deferred update
        this.pendingPresenceTimer = setTimeout(() => {
          this.client.setPresence({
            presence: newPresence,
            status_msg: '',
          }).then(() => {
            this.lastPresenceUpdate = Date.now();
            logger.info(`Deferred presence update to ${newPresence} for ${userId}`);
            this.pendingPresenceTimer = null;
          }).catch(error => {
            logger.error('PRESENCE: Deferred update failed', error);
            logger.warn('Deferred presence update failed:', error.message);
            this.pendingPresenceTimer = null;
          });
        }, timeUntilNext);
        
        return; // Skip immediate update
      }

      // Clear any pending timer since we're doing immediate update
      if (this.pendingPresenceTimer) {
        clearTimeout(this.pendingPresenceTimer);
        this.pendingPresenceTimer = null;
      }
      
      
      try {
        // Set presence on server
        await this.client.setPresence({
          presence: newPresence,
          status_msg: '',
        });
        
        this.presenceRetryCount = 0; // Reset retry count on success
        this.lastPresenceUpdate = Date.now(); // Record successful update
        logger.info(`Tab ${document.hidden ? 'hidden' : 'visible'} - set presence to ${newPresence} for ${userId}`);
        
        // Note: The Matrix SDK will emit presence events automatically via sync
        // No need to manually emit events - this was causing timing issues where
        // events were emitted before PresenceManager subscribed
      } catch (error) {
        // Handle 429 rate limit errors with exponential backoff
        if (error.errcode === 'M_LIMIT_EXCEEDED' || error.httpStatus === 429) {
          this.handlePresenceRateLimit(error, newPresence);
        } else {
          logger.error('PRESENCE: Error setting presence', error);
          logger.warn('Failed to update presence on visibility change:', error.message);
        }
      }
    };

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Defer initial presence update to avoid race conditions and 429 errors
    // Wait for sync to start before setting presence
    if (!document.hidden && this.sendPresenceUpdates) {
      const userId = this.client.getUserId();
      logger.debug('Scheduling initial presence update after sync starts');
      
      // Wait for sync to start (5 seconds delay to ensure client is fully ready)
      setTimeout(() => {
        // Double-check we're still visible and still want to send presence
        if (document.hidden || !this.sendPresenceUpdates || !this.client) {
          logger.debug('Skipping deferred initial presence - conditions changed');
          return;
        }
        
        // Throttle check to prevent 429 rate limit errors
        const now = Date.now();
        if (now - this.lastPresenceUpdate < this.minPresenceInterval) {
          logger.debug('Skipping deferred initial presence - too soon after last update');
          return;
        }
        
        // Set timestamp BEFORE async call to prevent race condition
        this.lastPresenceUpdate = now;
        
        this.client.setPresence({
          presence: 'online',
          status_msg: '',
        }).then(() => {
          this.presenceRetryCount = 0; // Reset retry count on success
          logger.info(`Set initial presence to online (tab is visible) for ${userId}`);
        }).catch(error => {
          // Handle 429 rate limit errors with exponential backoff
          if (error.errcode === 'M_LIMIT_EXCEEDED' || error.httpStatus === 429) {
            this.handlePresenceRateLimit(error, 'online');
          } else {
            // On other failures, reset timestamp to allow retry
            this.lastPresenceUpdate = 0;
            logger.error('PRESENCE: Failed to set initial presence', error);
            logger.warn('Failed to set initial presence:', error.message);
          }
        });
      }, 5000); // 5 second delay for initial presence
    } else if (alwaysAppearOffline && this.client) {
      // Set initial presence to offline for users who want to appear offline
      setTimeout(() => {
        if (this.client) {
          this.client.setPresence({
            presence: 'offline',
            status_msg: '',
          }).then(() => {
            logger.info('Set initial presence to offline (alwaysAppearOffline enabled)');
          }).catch(error => {
            logger.warn('Failed to set initial offline presence:', error.message);
          });
        }
      }, 5000);
    }

    // Store reference for cleanup
    this._visibilityChangeHandler = handleVisibilityChange;
  }

  /**
   * Handle rate limit errors for presence updates with exponential backoff
   * @param {Error} error - The error from the presence update
   * @param {string} presence - The presence status to set ('online' or 'unavailable')
   */
  handlePresenceRateLimit(error, presence) {
    if (this.presenceRetryCount >= this.maxPresenceRetries) {
      logger.warn(`Presence update failed after ${this.maxPresenceRetries} retries, giving up`);
      this.presenceRetryCount = 0; // Reset for next attempt
      this.lastPresenceUpdate = 0; // Allow future updates
      return;
    }

    this.presenceRetryCount++;
    // Exponential backoff: 60s, 120s, 240s
    const backoffDelay = 60000 * Math.pow(2, this.presenceRetryCount - 1);
    
    logger.warn(`Presence rate limited (429), retrying in ${backoffDelay / 1000}s (attempt ${this.presenceRetryCount}/${this.maxPresenceRetries})`);

    this.presenceRetryTimeoutId = setTimeout(() => {
      this.presenceRetryTimeoutId = null;

      if (!this.client || !this.sendPresenceUpdates) {
        logger.debug('Skipping presence retry - client or presence disabled');
        return;
      }

      this.client.setPresence({
        presence: presence,
        status_msg: '',
      }).then(() => {
        this.presenceRetryCount = 0; // Reset on success
        this.lastPresenceUpdate = Date.now();
        logger.info(`Presence retry successful: ${presence}`);
      }).catch(retryError => {
        if (retryError.errcode === 'M_LIMIT_EXCEEDED' || retryError.httpStatus === 429) {
          // Recursive retry with backoff
          this.handlePresenceRateLimit(retryError, presence);
        } else {
          logger.error('Presence retry failed with non-rate-limit error:', retryError);
          this.presenceRetryCount = 0;
          this.lastPresenceUpdate = 0;
        }
      });
    }, backoffDelay);
  }

  /**
   * Clean up presence timer
   * Called when logging out or destroying the client
   */
  cleanupPresenceTimer() {
    if (this.pendingPresenceTimer) {
      clearTimeout(this.pendingPresenceTimer);
      this.pendingPresenceTimer = null;
    }
    if (this.presenceRetryTimeoutId) {
      clearTimeout(this.presenceRetryTimeoutId);
      this.presenceRetryTimeoutId = null;
    }
  }

  /**
   * Start the Matrix client sync
   */
  async startSync(options = {}) {
    if (!this.client) {
      await this.initialize();
    }

    // Prevent double-starting sync
    if (this.isSyncStarted) {
      logger.info('Matrix sync already started, skipping duplicate start');
      return;
    }

    // Check if client is already running
    if (this.client.clientRunning) {
      logger.info('Matrix client already running, skipping sync start');
      this.isSyncStarted = true;
      return;
    }

    const syncOptions = {
      initialSyncLimit: 10,
      pollTimeout: 30000, // 30 second long polling
      disablePresence: true, // Disable presence in sync - handled by Page Visibility API instead
      lazyLoadMembers: true, // Optimize loading
      // Force more aggressive syncing
      threadPoolSize: 3,
      includeArchivedRooms: false,
      ...options,
    };

    try {
      logger.info('Starting Matrix sync');
      this.isSyncStarted = true;
      this.client.startClient(syncOptions);

      // Start periodic cleanup for pending DM cache (only once)
      if (!this.pendingDMCleanupInterval) {
        this.pendingDMCleanupInterval = setInterval(() => {
          this.cleanupPendingDMCache();
        }, 3600000); // 1 hour
        logger.debug('[matrixService] Started pending DM cleanup interval (1 hour)');
      }

      // Note: Presence is now handled by Page Visibility API in setupPageVisibilityHandling()
      // This ensures presence is only 'online' when tab is visible, not 24/7

      // Monitor sync state for debugging
      this.syncStateCheckTimer = setTimeout(() => {
        // Check if client still exists (may have been cleared during logout)
        if (!this.client) {
          logger.debug('Sync state check skipped - client is null');
          return;
        }

        const syncState = this.client.getSyncState();
        logger.debug('Sync state check:', {
          syncState: syncState,
          isReady: this.isReady(),
          isSyncing: this.isSyncing,
          isSyncStarted: this.isSyncStarted,
        });
      }, 5000);
    } catch (error) {
      logger.error('Failed to start Matrix sync:', error);
      this.isSyncStarted = false; // Reset flag on error
      throw error;
    }
  }

  /**
   * Stop the Matrix client
   */
  async stopSync() {
    if (this.client) {
      logger.debug('Stopping Matrix client');
      this.client.stopClient();
      this.isSyncing = false;
      this.isSyncStarted = false; // Reset sync started flag
    }
  }

  /**
   * Retry a backed-off sync request without stopping the active client.
   * Falls back to starting sync when the client exists but sync is not running.
   */
  async retrySyncImmediately() {
    if (!this.client) {
      logger.debug('retrySyncImmediately: No client available');
      return false;
    }

    if (typeof this.client.retryImmediately === 'function') {
      const didRetry = this.client.retryImmediately();
      logger.info(
        didRetry
          ? 'Requested immediate Matrix sync retry'
          : 'Matrix sync retry was requested, but no backed-off request was pending',
      );
      return didRetry;
    }

    if (!this.isSyncStarted && !this.client.clientRunning) {
      logger.info('Matrix sync is not running, starting sync instead of retrying');
      await this.startSync();
      return true;
    }

    logger.warn('Matrix client does not support retryImmediately and sync is already running');
    return false;
  }

  setSendPresenceUpdates(value) {
    this.sendPresenceUpdates = value;
  }

  /**
   * Set user presence (Undefined presenceType will reset forcing to offline)
   */
  async setUserPresence(presenceType, statusMsg) {
    logger.debug(`client ${!!this.client} and sendPresenceUpdates ${this.sendPresenceUpdates}`);
    if (!this.client || !this.sendPresenceUpdates) return;

    try {
      await this.client.setPresence({
        presence: presenceType,
        status_msg: statusMsg,
      });
      logger.debug(`Updated presence to: ${presenceType}`);

      // Dispatch event to update local presence cache (for UI refresh)
      const userId = this.client.getUserId();
      if (userId) {
        window.dispatchEvent(new CustomEvent('own-presence-changed', {
          detail: {
            userId,
            presence: presenceType,
            statusMsg: statusMsg || '',
          },
        }));
      }
    } catch (error) {
      logger.warn('Failed to update presence:', error.message);
    }
  }

  /**
   * Stop the Matrix client and wait for crypto to fully shutdown
   * This ensures all IndexedDB connections are closed before attempting deletion
   * Used during logout to prevent "blocked" errors when deleting crypto stores
   */
  async stopAndWaitForCryptoShutdown() {
    if (!this.client) {
      logger.debug('stopAndWaitForCryptoShutdown: No client to stop');
      return;
    }

    logger.debug('stopAndWaitForCryptoShutdown: Stopping client...');
    
    // Stop the client - wrapped in try-catch to ensure shutdown continues even if stopClient fails
    try {
      this.client.stopClient();
    } catch (stopError) {
      logger.debug('stopAndWaitForCryptoShutdown: Error stopping client (continuing shutdown):', stopError.message);
    }
    
    // Wait for sync to fully stop
    await new Promise(resolve => setTimeout(resolve, MatrixService.CRYPTO_SHUTDOWN_WAIT_MS));
    
    // Try to explicitly close crypto resources if available
    try {
      const crypto = this.client.getCrypto?.();
      if (crypto) {
        // The Rust crypto SDK may have a close method
        if (typeof crypto.close === 'function') {
          logger.debug('stopAndWaitForCryptoShutdown: Closing crypto...');
          await crypto.close();
        }
        // Some versions use stop instead
        if (typeof crypto.stop === 'function') {
          logger.debug('stopAndWaitForCryptoShutdown: Stopping crypto...');
          crypto.stop();
        }
      }
    } catch (cryptoCloseError) {
      logger.debug('stopAndWaitForCryptoShutdown: Error closing crypto (may be expected):', cryptoCloseError.message);
    }
    
    // Wait for IndexedDB connections to fully close
    await new Promise(resolve => setTimeout(resolve, MatrixService.CRYPTO_SHUTDOWN_WAIT_MS));
    
    // Remove all event listeners to ensure no dangling references
    this.client.removeAllListeners();
    
    this.isSyncing = false;
    this.isSyncStarted = false;
    
    logger.debug('stopAndWaitForCryptoShutdown: Client stopped and crypto shutdown complete');
  }

  /**
   * Cleanup method to be called on logout
   * Ensures complete cleanup of Matrix client and its resources
   */
  async cleanup() {
    try {
      // Clean up presence timer first
      this.cleanupPresenceTimer();

      // Stop dehydration FIRST to prevent null pointer errors during logout
      // This must happen before stopping the client
      if (this.client && this.dehydrationEnabled) {
        try {
          const crypto = this.client.getCrypto();
          if (crypto && typeof crypto.stopDehydration === 'function') {
            logger.debug('Cleanup: Stopping dehydration before client cleanup');
            await crypto.stopDehydration();
            logger.debug('Cleanup: Dehydration stopped successfully');
          }
        } catch (dehydrationError) {
          // Log but continue - don't let dehydration cleanup block logout
          logger.debug('Cleanup: Dehydration stop failed (may already be stopped):', 
            dehydrationError.message);
        }
        this.dehydrationEnabled = false;
        this.isDehydrating = false;
      }

      // Clean up dehydration event listeners
      this.cleanupDehydrationTriggers();

      // Remove the A_25514 isolation-policy re-arm listeners from the client they are actually bound
      // to (which may differ from this.client after a store-clear retry / re-login swap).
      this._detachIsolationReArm(this._isolationReArmClient);
      this._isolationReArmHandler = null;
      this._isolationReArmReevalHandler = null;
      this._isolationReArmAccountDataHandler = null;
      this._isolationReArmClient = null;

      // Set presence to offline before cleanup
      if (this.client && this.sendPresenceUpdates) {
        try {
          await this.client.setPresence({
            presence: 'offline',
            status_msg: '',
          });
          logger.info('Set presence to offline on cleanup');
        } catch (error) {
          logger.warn('Failed to set offline presence:', error.message);
        }
      }

      // Remove visibility change listener
      if (this._visibilityChangeHandler && typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', this._visibilityChangeHandler);
        this._visibilityChangeHandler = null;
        logger.debug('Removed visibility change listener');
      }

      // Clear any pending timers first to prevent them from accessing null client
      if (this.syncStateCheckTimer) {
        clearTimeout(this.syncStateCheckTimer);
        this.syncStateCheckTimer = null;
        logger.debug('Cleared sync state check timer');
      }

      // Clear pending DM cleanup interval
      if (this.pendingDMCleanupInterval) {
        clearInterval(this.pendingDMCleanupInterval);
        this.pendingDMCleanupInterval = null;
        logger.debug('[matrixService] Cleared pending DM cleanup interval');
      }

      // Clear all pending DM timers and cache
      for (const [, metadata] of this.pendingDMRooms.entries()) {
        if (metadata && metadata.timerId) {
          clearTimeout(metadata.timerId);
        }
      }
      this.pendingDMRooms.clear();
      logger.debug('[matrixService] Cleared pending DM cache');

      // Stop sync and crypto, then clear client
      if (this.client) {
        logger.info('Stopping Matrix sync for cleanup...');
        
        // Use the dedicated crypto shutdown method to properly close all connections
        // This prevents "blocked" errors when deleting IndexedDB during logout
        await this.stopAndWaitForCryptoShutdown();

        // Clear client reference
        this.client = null;
      }

      // Reset initialization flag
      this.isInitialized = false;

      // Reset dehydration flags
      this.dehydrationEnabled = false;
      this.isDehydrating = false;

      // Clear per-session service caches (tokens, tim-information classifications).
      // Dynamic import to avoid a circular dependency between matrixService and these
      // services (both import matrixService).
      try {
        const [{ default: fhirService }, { default: timInformationService }] = await Promise.all([
          import('./fhirService'),
          import('./timInformationService'),
        ]);
        fhirService.clearTokens();
        timInformationService.clearCache();
        logger.debug('[matrixService] Cleared session service caches');
      } catch (cacheErr) {
        logger.warn('[matrixService] Failed to clear session service caches:', cacheErr?.message);
      }

      logger.info('Matrix service cleanup completed');
    } catch (error) {
      logger.error('Error during Matrix service cleanup:', error);
    }
  }

  /**
   * Called when sync is prepared - load initial data
   */
  onSyncPrepared() {
    const rooms = this.getRooms();
    logger.info(`Matrix sync prepared. Found ${rooms.length} rooms`);

    const dmRooms = this.client.getAccountData('m.direct')?.getContent() || {};

    
    // Log room information for debugging
    rooms.forEach(room => {
      logger.debug('Room:', {
        id: room.roomId,
        name: room.name || 'Unnamed Room',
        memberCount: room.getJoinedMemberCount(),
        unread: room.getUnreadNotificationCount(),
        isOneToOne: Object.values(dmRooms).some(dmList => dmList.includes(room.roomId)),
        roomType: room.getJoinRule(),
      });
    });

    // Hooks will handle initial room loading
  }

  /**
   * Get all rooms (conversations)
   */
  getRooms() {
    if (!this.client) {
      logger.warn('Matrix client not initialized');
      return [];
    }
    return this.client.getRooms();
  }

  /**
   * Find existing DM room with a specific user
   * @param {string} userId - User ID to search for
   * @returns {Room|null} Existing DM room or null if not found
   */
  findDMForUser(userId) {
    if (!this.client) {
      logger.warn('Matrix client not initialized');
      return null;
    }

    try {
      const directEvent = this.client.getAccountData('m.direct');
      const directContent = directEvent?.getContent() || {};
      const roomIds = directContent[userId] || [];

      // Find first active DM room with this user that meets all criteria:
      // 1. Current user is joined
      // 2. Server reports exactly 2 members (lazy-loading-safe)
      // 3. Target user is present in local state
      for (const roomId of roomIds) {
        const room = this.client.getRoom(roomId);
        if (!room) continue;

        // Check if current user is joined
        if (room.getMyMembership() !== 'join') {
          logger.debug(`Skipping room ${roomId}: current user not joined`);
          continue;
        }

        // CRITICAL: Use server-reported counts (not affected by lazy loading)
        // getJoinedMemberCount()/getInvitedMemberCount() come from sync summary,
        // not local state. With lazyLoadMembers: true, getMembers() may return
        // only a subset of members, causing group rooms to look like 1-on-1 DMs.
        const joinedCount = room.getJoinedMemberCount() || 0;
        const invitedCount = room.getInvitedMemberCount() || 0;
        const serverMemberCount = joinedCount + invitedCount;

        if (serverMemberCount !== 2) {
          logger.debug(`Skipping room ${roomId}: server reports ${serverMemberCount} members (joined=${joinedCount}, invited=${invitedCount}), not a 1-on-1 DM`);
          continue;
        }

        // Verify target user is present in local state
        const allMembers = room.currentState.getMembers().filter(m =>
          m.membership === 'join' || m.membership === 'invite',
        );

        const targetMember = allMembers.find(m => m.userId === userId);
        if (!targetMember) {
          logger.debug(`Skipping room ${roomId}: target user ${userId} not found in local state (may be lazy loading)`);
          continue;
        }

        logger.debug(`Found valid existing 1-on-1 DM with ${userId}: ${roomId}`);
        return room;
      }

      logger.debug(`No valid 1-on-1 DM found with ${userId}`);
      return null;
    } catch (error) {
      logger.error('Error finding DM for user:', error);
      return null;
    }
  }

  /**
   * Set or unset a room as a direct message room
   * IMPORTANT: This is REQUIRED for proper DM functionality. The is_direct flag
   * on createRoom() does NOT automatically update m.direct account data.
   * 
   * Based on Element Web's implementation (matrix-react-sdk/src/Rooms.ts)
   * 
   * @param {string} roomId - The room ID to mark/unmark as DM
   * @param {string|null} userId - User ID to mark as DM partner, or null to unmark
   * @returns {Promise<void>}
   */
  async setDMRoom(roomId, userId) {
    logger.debug('[matrixService] setDMRoom - START - roomId:', roomId, 'userId:', userId);
    logger.debug('[matrixService] setDMRoom START - roomId:', roomId, 'userId:', userId);
    if (!this.client) {
      logger.error('[matrixService] ERROR - Matrix client not initialized in setDMRoom');
      throw new Error('Matrix client not initialized');
    }

    // Don't set DMs for guest users
    if (this.client.isGuest()) {
      logger.debug('[matrixService] Skipping DM room marking - user is guest');
      logger.debug('Skipping DM room marking for guest user');
      return;
    }

    // Capture timestamp once for consistent TTL tracking
    const originalTimestamp = Date.now();

    // Cancel existing timer if present (handle multiple calls for same room)
    if (this.pendingDMRooms.has(roomId)) {
      const existing = this.pendingDMRooms.get(roomId);
      if (existing && existing.timerId) {
        clearTimeout(existing.timerId);
        logger.debug(`[matrixService] setDMRoom: Cancelled existing timer for ${roomId}`);
      }
    }

    // Only add to pending cache if we're MARKING as DM (userId not null)
    // When unmarking (userId === null), we don't need to bridge any gap
    let successTimerId = null;
    if (userId) {
      // Create the timer FIRST, before adding to cache
      // This ensures the timer ID is available if AccountData event fires during server call
      successTimerId = setTimeout(() => {
        this.pendingDMRooms.delete(roomId);
        logger.debug(`[matrixService] setDMRoom: Removed ${roomId} from pending cache (60s timeout)`);
      }, 60000); // 60 seconds minimum TTL

      // Add to pending cache with timer ID BEFORE server update
      // This prevents race condition with AccountData event
      this.pendingDMRooms.set(roomId, { timestamp: originalTimestamp, timerId: successTimerId });
      logger.debug(`[matrixService] setDMRoom: Added ${roomId} to pending DM cache with 60s timer`);
    } else {
      // Unmarking - remove from pending cache if present
      this.pendingDMRooms.delete(roomId);
      logger.debug(`[matrixService] setDMRoom: Removing ${roomId} from pending cache (unmarking DM)`);
    }

    try {
      // Get current m.direct content
      logger.debug('[matrixService] Getting current m.direct account data');
      const directEvent = this.client.getAccountData('m.direct');
      const currentContent = directEvent?.getContent() || {};
      logger.debug('[matrixService] Current m.direct content:', JSON.stringify(currentContent, null, 2));

      const dmRoomMap = new Map(Object.entries(currentContent));
      let modified = false;

      // Remove this room from ALL users' DM lists
      // (a room can only be a DM with one person)
      logger.debug('[matrixService] Cleaning up room from other users DM lists (if present)');
      for (const [existingUserId, roomIds] of dmRoomMap.entries()) {
        if (existingUserId !== userId) {
          const indexOfRoom = roomIds.indexOf(roomId);
          if (indexOfRoom > -1) {
            logger.debug('[matrixService] Found room in user', existingUserId, 'DM list, removing');
            // Create new array without mutation
            const newRoomIds = roomIds.filter(id => id !== roomId);

            if (newRoomIds.length > 0) {
              dmRoomMap.set(existingUserId, newRoomIds);
            } else {
              // Clean up empty arrays to match Element Web behavior
              dmRoomMap.delete(existingUserId);
            }

            modified = true;
            logger.debug(`Removed room ${roomId} from ${existingUserId}'s DM list`);
          }
        }
      }

      // Add room to the target user's DM list
      if (userId) {
        logger.debug('[matrixService] Adding room to target user DM list - userId:', userId);
        const currentRoomList = dmRoomMap.get(userId) || [];
        logger.debug('[matrixService] Current room list for user', userId, ':', currentRoomList);
        if (!currentRoomList.includes(roomId)) {
          // Create NEW array instead of mutating - prevents SDK state corruption
          const newRoomList = [...currentRoomList, roomId];
          dmRoomMap.set(userId, newRoomList);
          modified = true;
          logger.debug('[matrixService] Room added to user DM list, new list:', newRoomList);
          logger.debug(`Added room ${roomId} to ${userId}'s DM list`);
        } else {
          logger.debug('[matrixService] Room already in user DM list');
        }
      }

      // Only call setAccountData if something actually changed
      if (!modified) {
        logger.debug('[matrixService] No changes to m.direct needed, skipping setAccountData');
        logger.debug('No changes needed to m.direct account data');
        return;
      }

      // Persist changes to account data
      const newMDirectContent = Object.fromEntries(dmRoomMap);
      logger.debug('[matrixService] About to call client.setAccountData with NEW m.direct content:', JSON.stringify(newMDirectContent, null, 2));
      logger.debug('[matrixService] Calling client.setAccountData for m.direct in matrixService.setDMRoom');

      await this.client.setAccountData('m.direct', newMDirectContent);
      logger.debug('[matrixService] client.setAccountData SUCCESS - server persisted the update');
      logger.info(`Successfully updated m.direct account data for room ${roomId} with user ${userId}`);

      // Success! Timer is already created and stored, nothing more to do

    } catch (error) {
      // Only handle timer cleanup if we created one (marking as DM)
      if (userId && successTimerId) {
        // Timer already created - clear it
        clearTimeout(successTimerId);

        // Distinguish between network errors and other errors
        const isNetworkError = error.name === 'ConnectionError' ||
                               error.message?.includes('network') ||
                               error.message?.includes('timeout') ||
                               error.message?.includes('ECONNREFUSED') ||
                               error.message?.includes('ETIMEDOUT');

        if (isNetworkError) {
          // Network error - keep in cache longer to allow retry/reconnection
          logger.debug(`[matrixService] setDMRoom: Network error, keeping ${roomId} in cache for recovery`);
          const networkTimerId = setTimeout(() => {
            this.pendingDMRooms.delete(roomId);
            logger.debug(`[matrixService] setDMRoom: Removed ${roomId} from pending cache (network recovery timeout)`);
          }, 120000); // 2 minutes for network recovery

          this.pendingDMRooms.set(roomId, { timestamp: originalTimestamp, timerId: networkTimerId });
        } else {
          // Other errors (permissions, validation, etc.) - remove from cache
          this.pendingDMRooms.delete(roomId);
          logger.debug(`[matrixService] setDMRoom: Removed ${roomId} from pending cache (error)`);
        }
      }

      logger.error('[matrixService] ERROR in setDMRoom:', error.message, error);
      logger.error('Error setting DM room:', error);
      throw error;
    }
  }

  /**
   * Clean up expired entries from pending DM cache
   * Called periodically (every 1 hour) to prevent memory leaks
   * @private
   */
  cleanupPendingDMCache() {
    const now = Date.now();
    const MAX_AGE = 3600000; // 1 hour maximum cache lifetime
    const MAX_CACHE_SIZE = 1000; // Maximum number of entries
    const expiredRooms = [];

    // Remove entries older than 1 hour
    for (const [roomId, metadata] of this.pendingDMRooms.entries()) {
      // Defensive check for corrupted entries
      if (!metadata || typeof metadata.timestamp !== 'number') {
        // Corrupted entry - clean up timer if exists and remove
        if (metadata && metadata.timerId) {
          clearTimeout(metadata.timerId);
        }
        this.pendingDMRooms.delete(roomId);
        logger.warn(`[matrixService] Cleaned up corrupted pending DM entry: ${roomId}`);
        continue;
      }

      if (now - metadata.timestamp > MAX_AGE) {
        // Clear timer if it exists
        if (metadata.timerId) {
          clearTimeout(metadata.timerId);
        }
        expiredRooms.push(roomId);
      }
    }

    expiredRooms.forEach(roomId => {
      this.pendingDMRooms.delete(roomId);
      logger.debug(`[matrixService] Cleaned up expired pending DM (1 hour): ${roomId}`);
    });

    // Enforce max cache size (remove oldest if over limit)
    let sizeRemovedCount = 0;
    if (this.pendingDMRooms.size > MAX_CACHE_SIZE) {
      const entries = Array.from(this.pendingDMRooms.entries())
        .sort((a, b) => a[1].timestamp - b[1].timestamp);

      const toRemove = entries.slice(0, this.pendingDMRooms.size - MAX_CACHE_SIZE);
      toRemove.forEach(([roomId, metadata]) => {
        if (metadata.timerId) {
          clearTimeout(metadata.timerId);
        }
        this.pendingDMRooms.delete(roomId);
        logger.debug(`[matrixService] Cleaned up pending DM (max size): ${roomId}`);
      });
      sizeRemovedCount = toRemove.length;
    }

    const totalCleaned = expiredRooms.length + sizeRemovedCount;
    logger.debug(`[matrixService] cleanupPendingDMCache: Removed ${totalCleaned} entries, ${this.pendingDMRooms.size} remaining`);

    return totalCleaned;
  }

  /**
   * Add a room to the pending DM cache before joining.
   *
   * This is critical for preventing the "disappearing conversation" bug:
   * When auto-accepting a DM invite, there's a race condition window where:
   * 1. Invite arrives (membership='invite', is_direct=true) → room appears
   * 2. client.joinRoom() is called → membership changes to 'join'
   * 3. updateRooms() runs before setDMRoom() completes
   * 4. isDMRoom() returns false (not in m.direct, is_direct check skipped for joined rooms)
   * 5. Room disappears from conversation list!
   *
   * By adding the room to pendingDMRooms BEFORE joining, we ensure isDMRoom()
   * will return true during this race window.
   *
   * @param {string} roomId - The room ID to add to pending cache
   */
  addPendingDMRoom(roomId) {
    if (!roomId) return;

    const originalTimestamp = Date.now();

    // Check if already in cache (don't overwrite existing timer)
    if (this.pendingDMRooms.has(roomId)) {
      logger.debug(`[matrixService] addPendingDMRoom: ${roomId} already in pending cache`);
      return;
    }

    // Add with a 60-second TTL (same as setDMRoom)
    const timerId = setTimeout(() => {
      this.pendingDMRooms.delete(roomId);
      logger.debug(`[matrixService] addPendingDMRoom: Removed ${roomId} from pending cache (60s timeout)`);
    }, 60000);

    this.pendingDMRooms.set(roomId, { timestamp: originalTimestamp, timerId });
    logger.debug(`[matrixService] addPendingDMRoom: Added ${roomId} to pending DM cache`);
  }

  /**
   * Remove a room from the pending DM cache
   * Called when accepting a DM invite fails or when the room is no longer needed
   *
   * @param {string} roomId - The room ID to remove
   */
  removePendingDMRoom(roomId) {
    if (!roomId) {
      logger.warn('[matrixService] removePendingDMRoom: No roomId provided');
      return;
    }

    const metadata = this.pendingDMRooms.get(roomId);
    if (metadata) {
      // Clear the timeout timer to prevent memory leaks
      if (metadata.timerId) {
        clearTimeout(metadata.timerId);
      }
      this.pendingDMRooms.delete(roomId);
      logger.debug(`[matrixService] removePendingDMRoom: Removed ${roomId} from pending DM cache`);
    } else {
      logger.debug(`[matrixService] removePendingDMRoom: ${roomId} not found in pending cache`);
    }
  }

  /**
   * Check if a room is marked as a direct message (DM) room
   *
   * This function checks TWO sources to handle both joined rooms and invites:
   * 1. m.direct account data (for joined/created DMs)
   * 2. is_direct flag in member event (for DM invites not yet accepted)
   *
   * This dual check ensures DM invites appear in the Conversations page
   * even before the invitee accepts and updates their m.direct.
   *
   * @param {string} roomId - The room ID to check
   * @returns {boolean} True if the room is a DM (in m.direct OR has is_direct flag), false otherwise
   */
  isDMRoom(roomId) {
    if (!this.client) {
      logger.warn('Matrix client not initialized');
      return false;
    }

    try {
      // Check 1: m.direct account data (for joined DMs)
      const directEvent = this.client.getAccountData('m.direct');
      const directContent = directEvent?.getContent() || {};

      const inMDirect = Object.values(directContent).some((roomIds) =>
        Array.isArray(roomIds) && roomIds.includes(roomId),
      );

      if (inMDirect) {
        return true;
      }

      // Check 2: Pending DM rooms (recently accepted invites during m.direct update gap)
      // This prevents rooms from disappearing during the race condition window
      // Cleanup is handled by periodic audit (every 1 hour)
      if (this.pendingDMRooms.has(roomId)) {
        logger.debug(`[matrixService] isDMRoom: ${roomId} found in pending DM cache`);
        return true;
      }

      // Check 3: is_direct flag in member event (for DM invites not yet accepted)
      // This handles the case where User A creates a DM with User B:
      // - User A's m.direct is updated immediately (handled above)
      // - User B receives an invite with is_direct=true but m.direct NOT updated yet
      // - We check the is_direct flag so User B sees it in Conversations, not Channels
      const room = this.client.getRoom(roomId);
      if (!room) {
        return false;
      }

      const myUserId = this.client.getUserId();
      if (!myUserId) {
        return false;
      }

      const membership = room.getMyMembership();

      // Only check is_direct flag for invites
      // For joined rooms, m.direct should already be updated (via auto-accept)
      if (membership === 'invite' && room.currentState) {
        const memberEvent = room.currentState.getStateEvents('m.room.member', myUserId);
        if (memberEvent) {
          const content = memberEvent.getContent();
          const isDirect = content.is_direct === true;
          if (isDirect) {
            logger.debug(`isDMRoom: Room ${roomId} is a DM invite (is_direct=true)`);
            return true;
          }
        }
      }

      return false;
    } catch (error) {
      logger.error('Error checking if room is DM:', error);
      return false;
    }
  }

  /**
   * Get the OTHER user ID in a DM room from m.direct account data.
   * This is more reliable than using getJoinedMembers() during membership transitions.
   * 
   * @param {string} roomId - The room ID to look up
   * @returns {string|null} - The other user's ID, or null if not found or not a DM
   */
  getDMUserForRoom(roomId) {
    if (!this.client) {
      logger.warn('Matrix client not initialized');
      return null;
    }

    try {
      const dmRooms = this.client.getAccountData('m.direct')?.getContent() || {};

      // Iterate through { userId: [roomIds] } structure
      for (const [userId, roomIds] of Object.entries(dmRooms)) {
        if (Array.isArray(roomIds) && roomIds.includes(roomId)) {
          return userId;
        }
      }

      return null;
    } catch (error) {
      logger.error('Error getting DM user for room:', error);
      return null;
    }
  }

  /**
   * Check if a room is a valid 1-to-1 DM room
   * A valid DM must be in m.direct AND have exactly 2 members
   * This is useful for filtering out "DM rooms" that have had additional members added
   *
   * @param {string} roomId - The room ID to check
   * @returns {boolean} True if the room is a proper 1-to-1 DM (in m.direct with 2 members)
   */
  isValidDMRoom(roomId) {
    if (!this.client) {
      logger.warn('Matrix client not initialized');
      return false;
    }

    try {
      // First check if it's in m.direct
      const inMDirect = this.isDMRoom(roomId);
      if (!inMDirect) {
        return false;
      }

      // Then check if it has exactly 2 members
      const room = this.client.getRoom(roomId);
      if (!room) {
        return false;
      }

      const memberCount = room.getJoinedMemberCount();
      return memberCount === 2;
    } catch (error) {
      logger.error('Error checking if room is valid DM:', error);
      return false;
    }
  }

  /**
   * Check if client is ready
   */
  isReady() {
    return this.isInitialized && this.isSyncing && this.syncState === 'PREPARED';
  }

  /**
   * Add event listener
   */
  on(event, callback) {
    switch (event) {
      case 'roomsUpdated':
        this.roomListeners.push(callback);
        break;
      case 'syncStateChanged':
        this.syncListeners.push(callback);
        break;
      case 'messageReceived':
        this.messageListeners.push(callback);
        break;
      default:
        logger.warn(`Unknown event type: ${event}`);
    }
  }

  /**
   * Remove event listener
   */
  off(event, callback) {
    switch (event) {
      case 'roomsUpdated':
        this.roomListeners = this.roomListeners.filter(listener => listener !== callback);
        break;
      case 'syncStateChanged':
        this.syncListeners = this.syncListeners.filter(listener => listener !== callback);
        break;
      case 'messageReceived':
        this.messageListeners = this.messageListeners.filter(listener => listener !== callback);
        break;
      default:
        logger.warn(`Unknown event type: ${event}`);
    }
  }

  /**
   * Remove all listeners
   */
  removeAllListeners() {
    this.syncListeners = [];
    this.roomListeners = [];
    this.messageListeners = [];
  }

  /**
   * Recovery-related wrapper methods for key backup, SSSS, and cross-signing
   * These methods wrap the matrix-js-sdk APIs and handle errors/logging appropriately
   */

  /**
   * Get the current key backup version from the server
   * @returns {Promise<Object|null>} Backup version info or null if none exists
   */
  async getBackupVersion() {
    if (!this.client) {
      throw new Error('Matrix client not initialized');
    }

    try {
      const crypto = this.client.getCrypto();
      if (!crypto) {
        logger.warn('Crypto not available');
        return null;
      }

      const backupInfo = await crypto.getKeyBackupInfo();
      logger.debug('Got backup version:', backupInfo?.version);
      return backupInfo;
    } catch (error) {
      logger.error('Error getting backup version:', error.message);
      throw error;
    }
  }

  /**
   * Check if key backup is enabled and active
   * @returns {Promise<boolean>} True if backup is enabled
   */
  async isKeyBackupEnabled() {
    if (!this.client) {
      throw new Error('Matrix client not initialized');
    }

    try {
      const crypto = this.client.getCrypto();
      if (!crypto) {
        logger.warn('Crypto not available');
        return false;
      }

      const activeVersion = await crypto.getActiveSessionBackupVersion();
      const isEnabled = activeVersion !== null && activeVersion !== undefined;
      logger.debug('Key backup enabled:', isEnabled, 'version:', activeVersion);
      return isEnabled;
    } catch (error) {
      logger.error('Error checking if backup is enabled:', error.message);
      return false;
    }
  }

  /**
   * Restore key backup using a recovery key
   * @param {string} recoveryKey - The recovery key
   * @param {string} version - The backup version to restore
   * @param {Object} options - Options including progressCallback and signal
   * @returns {Promise<void>}
   */
  async restoreWithRecoveryKey(recoveryKey, version, options = {}) {
    if (!this.client) {
      throw new Error('Matrix client not initialized');
    }

    const { progressCallback, signal } = options;

    const crypto = this.client.getCrypto();
    if (!crypto) {
      throw new Error('Crypto not available');
    }

    try {
      // Load the megolm backup DECRYPTION KEY before restoring. This is the step the
      // live recovery flow used to skip: crypto.restoreKeyBackup() reads the decryption
      // key from the crypto store (olmMachine.getBackupKeys()) and throws "No decryption
      // key found in crypto store" if it was never loaded — which left historical
      // messages permanently UTD after a fresh-device login.
      //
      // This app ALWAYS stores a separate, random megolm backup key INSIDE secret storage
      // (SecretStorageService.bootstrapSSSS uses setupNewKeyBackup:true), so the recovery
      // key — which is the 4S key — is NOT the backup key. The only correct way to obtain
      // the backup key is loadSessionBackupPrivateKeyFromSecretStorage(): it reads
      // m.megolm_backup.v1 via our getSecretStorageKey cryptoCallback (which serves the 4S
      // key bytes from recoveryEphemeralStore), validates it against the server backup, and
      // caches the decryption key. It needs no recovery-key string, so this also covers the
      // restore() path which only holds key bytes. A legacy "recovery key IS the backup key"
      // fallback was deliberately removed: for this app's topology it can only ever cache a
      // WRONG key and fail, so a clear propagated error beats a misleading fallback.
      await crypto.loadSessionBackupPrivateKeyFromSecretStorage();
      logger.debug('Loaded backup decryption key from secret storage');

      // Check abort signal
      if (signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }

      // Sanity gate: restoreKeyBackup throws without a cached decryption key.
      const loadedKey = await crypto.getSessionBackupPrivateKey();
      if (!loadedKey) {
        throw new Error('Backup decryption key unavailable after load');
      }

      // Restore the backup
      logger.debug('Starting backup restoration for version:', version);

      // restoreKeyBackup expects an OPTIONS OBJECT and reads opts.progressCallback.
      // Passing a bare function (the previous bug) silently dropped all progress events.
      await crypto.restoreKeyBackup(progressCallback ? { progressCallback } : undefined);

      logger.debug('Backup restoration completed');
    } catch (error) {
      if (error.name === 'AbortError') {
        logger.debug('Backup restoration aborted');
        throw error;
      }
      // Sanitize error to avoid logging recovery key
      logger.error('Error restoring backup:', error.message);
      throw error;
    }
  }

  /**
   * Get the default SSSS key ID
   * @returns {Promise<string|null>} The default key ID or null
   */
  async getSSSSDefaultKey() {
    if (!this.client) {
      throw new Error('Matrix client not initialized');
    }

    try {
      const defaultKeyData = this.client.getAccountData('m.secret_storage.default_key');
      const defaultKeyId = defaultKeyData?.getContent()?.key;
      logger.debug('SSSS default key ID:', defaultKeyId);
      return defaultKeyId || null;
    } catch (error) {
      logger.error('Error getting SSSS default key:', error.message);
      return null;
    }
  }

  /**
   * Store a secret in secret storage
   * @param {string} name - Secret name
   * @param {string} data - Secret data
   * @param {string} keyId - Key ID to use for encryption
   * @returns {Promise<void>}
   */
  async putSecret(name, data, keyId) {
    if (!this.client) {
      throw new Error('Matrix client not initialized');
    }

    try {
      const secretStorage = this.client.getSecretStorage();
      if (!secretStorage) {
        throw new Error('Secret storage not available');
      }

      await secretStorage.store(name, data, keyId);
      logger.debug('Stored secret:', name);
    } catch (error) {
      logger.error('Error storing secret:', error.message);
      throw error;
    }
  }

  /**
   * Delete a device (sign out a specific session)
   * Uses Matrix User-Interactive Authentication (UIA) flow
   * HTTP cookies handle UIA session persistence automatically
   *
   * Second argument supports three shapes for backward compatibility:
   *   - null/undefined      → initial attempt, no auth
   *   - string              → legacy password retry
   *   - { ssoSession: id }  → SSO retry after fallback popup resolved
   *
   * @param {string} deviceId - Device ID to delete
   * @param {string|Object|null} authParams - Password string or SSO session wrapper
   * @returns {Promise<Object>} Result with success status
   */
  async deleteDevice(deviceId, authParams = null) {
    if (!this.client) {
      throw new Error('Matrix client not initialized');
    }

    if (!deviceId) {
      throw new Error('Device ID is required');
    }

    // Normalize second arg into discrete modes
    const password = typeof authParams === 'string' ? authParams : null;
    const ssoSession = authParams && typeof authParams === 'object' ? authParams.ssoSession : null;

    // SSO retry path: server already holds a UIA session marked sso-complete,
    // so we pass only { session } and let 401s bubble so the caller can poll.
    if (ssoSession) {
      await this.client.deleteDevice(deviceId, { session: ssoSession });
      logger.info('Device deleted successfully via SSO UIA retry');
      return { success: true, message: 'Device signed out successfully' };
    }

    try {
      // First attempt: Try without auth (will use HTTP cookie if valid)
      await this.client.deleteDevice(deviceId);

      logger.info('Device deleted successfully (existing UIA session)');
      return { success: true, message: 'Device signed out successfully' };

    } catch (error) {
      // Check if this is a UIA challenge (401 with flows)
      if (error.httpStatus === 401 && error.data?.flows) {
        const flows = error.data.flows || [];
        const session = error.data.session || null;
        const hasSsoFlow = flows.some(f => f.stages?.includes('m.login.sso'));
        const hasPasswordFlow = flows.some(f => f.stages?.includes('m.login.password'));
        // Prefer SSO when the server advertises SSO AND either:
        //   (a) the user logged in via SSO this session (persistent check — survives
        //       the 10-minute credential-cache TTL), or
        //   (b) the server only advertises SSO (SSO-only accounts).
        const preferSso = hasSsoFlow && (isSsoLoggedInSession() || !hasPasswordFlow);

        if (preferSso && !password) {
          logger.debug('UIA challenge received, SSO re-authentication required');
          return {
            success: false,
            needsSsoAuth: true,
            session,
            flows,
            message: 'SSO re-authentication required to sign out device',
          };
        }

        logger.debug('UIA challenge received, password required');

        // Signal UI to prompt for password if not provided
        if (!password) {
          return {
            success: false,
            needsPassword: true,
            session,
            flows,
            message: 'Password required to sign out device',
          };
        }

        // Retry with password authentication
        try {
          const userId = this.client.getUserId();
          const authData = {
            type: 'm.login.password',
            session,  // From 401 error
            identifier: {
              type: 'm.id.user',
              user: userId,
            },
            password: password,
          };

          await this.client.deleteDevice(deviceId, authData);

          logger.info('Device deleted successfully with password');
          // HTTP cookie is now set by server, valid for ~5 minutes
          return { success: true, message: 'Device signed out successfully' };

        } catch (authError) {
          logger.error('Password authentication failed:', authError.errcode);

          // Handle specific auth errors
          if (authError.errcode === 'M_FORBIDDEN') {
            return {
              success: false,
              errorCode: 'INCORRECT_PASSWORD',
              message: 'Incorrect password',
              error: authError,
            };
          }

          throw authError;  // Re-throw unexpected errors
        }
      }

      // Not a UIA challenge - handle other errors
      logger.error('Device deletion failed:', error.message, error.errcode);

      let errorMessage = 'Failed to sign out device';
      if (error.errcode === 'M_NOT_FOUND') {
        errorMessage = 'Device not found';
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        message: errorMessage,
        error: error,
      };
    }
  }

  /**
   * Setup event handler for session invalidation detection
   * Automatically logs out when token is invalidated by another session
   */
  setupSessionInvalidationHandler() {
    if (!this.client) return;

    logger.debug('Setting up session invalidation detection');

    // NED-4397: snapshot the login nonce of the session THIS client belongs to,
    // so a stale/zombie invalidation fired by a superseded client carries the
    // OLD nonce (which the consumer-side guard rejects) rather than the CURRENT
    // nonce it would otherwise read at teardown time. matrixAuthService is
    // already module-cached (login used it), so this resolves on the next
    // microtask — long before any network-driven Sync error. If it somehow has
    // not resolved, the handler passes `undefined` and handleSessionInvalidated
    // falls back to a live read (safe: no re-login has happened yet at that point).
    let ownerNonce;
    import('./matrixAuthService')
      .then((m) => { ownerNonce = m.default?.getLoginNonce?.(); })
      .catch(() => { /* leave undefined; handler falls back to a live read */ });

    // Listen for sync errors indicating token invalidation. The listener is
    // torn down by cleanup()'s client.removeAllListeners() on logout, so a
    // revoked old client cannot re-fire into a new session.
    this.client.on(ClientEvent.Sync, (state, prevState, data) => {
      if (state === 'ERROR' && data?.error) {
        const error = data.error;

        // Only invalidate on explicit token revocation (M_UNKNOWN_TOKEN)
        // Let the SDK handle normal 401s (expired tokens) with its built-in refresh
        // This prevents spurious logouts when waking from sleep with expired access token
        if (error.errcode === 'M_UNKNOWN_TOKEN') {
          logger.warn('Session token invalidated by server, forcing logout');
          this.handleSessionInvalidated(ownerNonce);
        } else if (error.httpStatus === 401) {
          // Don't invalidate - SDK will attempt token refresh automatically
          logger.debug('Received 401, SDK will attempt token refresh');
        }
      }
    });
  }

  /**
   * Handle session invalidation (token revoked, logged out elsewhere, etc.)
   */
  async handleSessionInvalidated(ownerNonce) {
    logger.info('Session invalidated - performing automatic logout');

    // Prevent duplicate logout calls
    if (this._loggingOut) {
      logger.debug('Logout already in progress, skipping');
      return;
    }

    this._loggingOut = true;

    const detail = { reason: 'Token invalidated by another session' };
    // NED-4397: whether a newer login superseded this teardown (computed after
    // logout()); read in the finally to decide whether to dispatch.
    let superseded = false;

    try {
      // Single dynamic import — the module is needed for both the logout()
      // call and the downstream recoveryActions.reset(). Module cache would
      // make a second import cheap, but one hoisted binding is clearer and
      // guarantees the reset step runs against the same instance.
      let matrixAuthService = null;
      try {
        matrixAuthService = (await import('./matrixAuthService')).default;
      } catch (importErr) {
        logger.error('handleSessionInvalidated: failed to import matrixAuthService:', importErr);
      }

      // NED-4397: the nonce of the session being invalidated. Prefer the owner
      // nonce captured at handler registration (correct for a zombie old-client
      // re-fire, which must NOT read the live/current nonce); fall back to a
      // live read if the caller didn't supply one.
      const invalidatedNonce = (typeof ownerNonce === 'number')
        ? ownerNonce
        : matrixAuthService?.getLoginNonce?.();
      if (typeof invalidatedNonce === 'number') {
        detail.loginNonce = invalidatedNonce;
      }

      // NED-4397 (P1 defensive): if this invalidation belongs to a SUPERSEDED
      // session (a newer login already advanced the nonce), do NOT tear down.
      // logout()/cleanup() act on the LIVE singleton client + creds, so running
      // them here would destroy the newly-authenticated session while the
      // post-hoc skip suppresses the UI transition. Gate the TEARDOWN itself, not
      // just the flag/dispatch. (Verified unreachable via the sync path today —
      // the SDK stops the old client's sync loop one-shot on M_UNKNOWN_TOKEN and
      // cleanup() removes the handler — but gated for defense-in-depth and parity
      // with the refresh-401 path, which IS reachable.)
      if (matrixAuthService
          && isSupersededInvalidation({ loginNonce: invalidatedNonce }, matrixAuthService.getLoginNonce())) {
        // Set `superseded` and return: the `return` exists solely to route
        // through the `finally` (which resets `_loggingOut`) with `superseded`
        // true so the dispatch is skipped. Do NOT add logic between here and the
        // return that assumes the teardown ran.
        superseded = true;
        logger.debug(
          `handleSessionInvalidated for a superseded session (nonce ${invalidatedNonce} < `
          + `${matrixAuthService.getLoginNonce()}); not tearing down the live session`,
        );
        try { sessionStorage.removeItem('matrix:session-invalidated'); } catch { /* ignore */ }
        return;
      }

      // Best-effort server/local logout; errors must NOT prevent the dispatch below.
      try {
        if (matrixAuthService) {
          await matrixAuthService.logout();
        }
      } catch (logoutErr) {
        logger.error('handleSessionInvalidated: logout failed (continuing):', logoutErr);
      }

      // NED-4397: the at-entry superseded case is short-circuited above; this
      // only catches the teardown-tail race — a re-login that COMPLETED while
      // logout() was in flight. Same canonical strict-`<` test as the gate.
      superseded = isSupersededInvalidation(
        { loginNonce: invalidatedNonce },
        matrixAuthService?.getLoginNonce?.(),
      );

      if (superseded) {
        logger.debug(
          'handleSessionInvalidated superseded by a newer login mid-teardown '
          + `(nonce ${invalidatedNonce} -> ${matrixAuthService?.getLoginNonce?.()}); skipping flag + dispatch`,
        );
        try { sessionStorage.removeItem('matrix:session-invalidated'); } catch { /* ignore */ }
      } else {
        // matrixAuthService.logout() clears the sessionStorage flag during its
        // local-cleanup phase; re-assert it AFTER so LoginPage's mount-time
        // reader still shows the "Session Expired" modal. ORDER MATTERS: the
        // dispatch below triggers React unmount/remount of ProtectedRoute →
        // LoginPage; LoginPage's effect reads the flag on mount. An earlier
        // attempt to hoist the dispatch above logout() raced the unmount
        // against logout's ~1s flag-removal window and intermittently showed
        // no popup.
        try {
          sessionStorage.setItem('matrix:session-invalidated', JSON.stringify({
            ...detail,
            timestamp: Date.now(),
          }));
        } catch (e) {
          logger.debug('Could not set session invalidation flag:', e);
        }

        // NED-3466 follow-up: explicitly reset the RecoveryProvider's reducer
        // BEFORE the dispatch so the remount can't pick up a stale step/error
        // from the outgoing session. Removes any dependency on whether
        // ProtectedRoute keeps the provider mounted across the transition.
        try {
          matrixAuthService?.recoveryActions?.reset?.();
        } catch (resetErr) {
          logger.debug('handleSessionInvalidated: recoveryActions.reset failed (non-fatal):', resetErr);
        }
      }
    } finally {
      // Dispatch only for a non-superseded (current-session) invalidation. A
      // superseded one must not flip auth / pop the modal over the new session.
      // (Not swallowed by logout/flag errors: those never set `superseded`.)
      if (!superseded) {
        window.dispatchEvent(new CustomEvent('matrix:session-invalidated', { detail }));
      }
      this._loggingOut = false;
    }
  }

  /**
   * Setup listener for incoming verification requests
   * Delegates to verificationRequestManager for smart toast notifications
   */
  setupVerificationListener() {
    if (!this.client) return;

    logger.debug('Setting up verification request listener');

    // Listen for incoming verification requests
    this.client.on(CryptoEvent.VerificationRequestReceived, async (request) => {
      // Debug: log all available properties on the request object
      logger.debug('Verification request object keys:', Object.keys(request));
      logger.debug('Verification request full dump:', {
        otherUserId: request.otherUserId,
        userId: request.userId,
        otherDeviceId: request.otherDeviceId,
        deviceId: request.deviceId,
        initiatedByMe: request.initiatedByMe,
        isSelfVerification: request.isSelfVerification,
        phase: request.phase,
        transactionId: request.transactionId,
        // Try channel properties
        channelTransactionId: request.channel?.transactionId,
        channelDeviceId: request.channel?.deviceId,
      });
      logger.info('Incoming device verification request:', {
        userId: request.otherUserId || request.userId,
        deviceId: request.otherDeviceId || request.deviceId,
      });

      // Import verification request manager dynamically to avoid circular deps
      try {
        const { default: verificationRequestManager } = await import('./verificationRequestManager');
        await verificationRequestManager.handleIncomingRequest(request);
      } catch (error) {
        logger.error('MatrixService: Error handling verification request:', error);
      }
    });

    logger.info('Verification request listener active');
  }

  /**
   * Get cross-signing status
   * @returns {Promise<Object>} Cross-signing status
   */
  async getCrossSigningStatus() {
    if (!this.client) {
      throw new Error('Matrix client not initialized');
    }

    try {
      const crypto = this.client.getCrypto();
      if (!crypto) {
        return { ready: false, publicKeysOnDevice: false };
      }

      // Check various methods depending on SDK version
      let status = { ready: false, publicKeysOnDevice: false };

      if (crypto.getCrossSigningStatus) {
        status = await crypto.getCrossSigningStatus();
      } else if (crypto.isCrossSigningReady) {
        status.ready = await crypto.isCrossSigningReady();
        status.publicKeysOnDevice = status.ready;
      } else if (this.client.getCrossSigningId) {
        const id = this.client.getCrossSigningId();
        status.ready = !!id;
        status.publicKeysOnDevice = !!id;
      }

      logger.debug('Cross-signing status:', status);
      return status;
    } catch (error) {
      logger.error('Error getting cross-signing status:', error.message);
      return { ready: false, publicKeysOnDevice: false };
    }
  }

  /**
   * Test-driver helper — download + decrypt an attachment by mxc URL.
   *
   * Mirrors the WebApp's own attachment-display path (useMediaThumbnail →
   * useFileDownload.downloadFileToDisplay → decryptMediaBlob) but skips the
   * React/cache wiring so it can be called from `window.__matrixService`
   * inside playwright `page.evaluate`. The gematik test-driver's
   * GET /devices/:d/media/authenticated/:fileId route needs decrypted bytes
   * to satisfy MediaGlue#checkMediaIntegrity, which compares them to the
   * on-disk plaintext fixture (bild.jpg). Reimplementing decrypt in the
   * driver would risk drifting from the WebApp's `matrix-encrypt-attachment`
   * version and miss any future header/auth changes baked into `customFetch`.
   *
   * Locates the encryption descriptor (content.file: {key, iv, hashes, v})
   * by walking the live timeline of every joined room for an event whose
   * content references this mxc URL. Cert receive-side scenarios always
   * call this AFTER the recipient has joined+synced the room, so the event
   * is in the live timeline. If no matching encrypted event is found, the
   * raw bytes are returned (unencrypted attachment path).
   *
   * @param {Object} params
   * @param {string} params.mxcUrl - mxc://server/mediaId
   * @returns {Promise<{base64: string, hadEncryptionInfo: boolean}>} Base64-
   *   encoded plaintext bytes plus a flag indicating whether the located
   *   timeline event had `content.file` (i.e. the room was E2E and the
   *   returned bytes are post-decrypt). The flag lets the test-driver
   *   distinguish "encrypted attachment, decrypt OK" from "no encryption
   *   descriptor found, raw passthrough" — important when an encrypted
   *   decrypt later throws, so the driver can fail loud instead of silently
   *   serving ciphertext from a fallback raw fetch. Base64 transport chosen
   *   because page.evaluate serializes raw Uint8Array as a plain object
   *   (slow + indexed by key), while base64 stays compact and lossless.
   */
  async decryptAttachment({ mxcUrl }) {
    if (!this.client) throw new Error('No matrix client');
    if (!mxcUrl || typeof mxcUrl !== 'string' || !mxcUrl.startsWith('mxc://')) {
      throw new Error(`Invalid mxc URI: ${mxcUrl}`);
    }

    // Walk timelines for the encryption descriptor. Stop on first encrypted
    // hit; if only unencrypted refs are found, we'll skip decrypt below.
    // Also capture content.info.mimetype while we have the event in hand —
    // decryptMediaBlob uses it to tag the intermediate Blob (purely
    // informational for our caller, since we base64-encode the bytes).
    let encryptionInfo = null;
    let mimeType;
    for (const room of this.client.getRooms()) {
      const events = room.getLiveTimeline().getEvents() || [];
      for (const e of events) {
        const content = e.getClearContent() || e.getContent() || {};
        if (content.file?.url === mxcUrl) {
          encryptionInfo = content.file;
          mimeType = content.info?.mimetype;
          break;
        }
      }
      if (encryptionInfo) break;
    }

    // Authenticated v1.11 endpoint — same one useFileDownload's
    // downloadFileToDisplay resolves to for encrypted media. Synapse
    // tenants reject the legacy /_matrix/media/v3 path for v1.11+ clients.
    const match = mxcUrl.match(/^mxc:\/\/([^/]+)\/(.+)$/);
    if (!match) throw new Error(`Invalid mxc URI: ${mxcUrl}`);
    const [, serverName, mediaId] = match;
    const baseUrl = this.client.getHomeserverUrl();
    const url = `${baseUrl}/_matrix/client/v1/media/download/${serverName}/${mediaId}`;

    // customFetch supplies the Bearer token + X-TIM-User-Agent (the SBG
    // gate rejects requests without the latter on tim-pro tenants).
    const resp = await this.customFetch(url, { method: 'GET' });
    if (!resp.ok) {
      throw new Error(`Media download failed: ${resp.status} ${resp.statusText}`);
    }
    const encryptedArrayBuffer = await resp.arrayBuffer();

    let arrayBufferOut;
    if (encryptionInfo) {
      // decryptMediaBlob takes a blob URL because the WebApp's hooks pipe
      // through blob URLs from useFileDownload. Synthesize one here and let
      // decryptMediaBlob revoke it in its `finally`. The decrypted blob is
      // the source of truth for bytes; revoke its URL in our own `finally`
      // so a throw from `arrayBuffer()` (OOM / detached blob) doesn't leak
      // the URL for the page lifetime.
      const encryptedBlob = new Blob([encryptedArrayBuffer]);
      const encryptedBlobUrl = URL.createObjectURL(encryptedBlob);
      const { decryptedBlob, decryptedBlobUrl } = await decryptMediaBlob(
        encryptedBlobUrl,
        encryptionInfo,
        mimeType,
      );
      try {
        arrayBufferOut = await decryptedBlob.arrayBuffer();
      } finally {
        URL.revokeObjectURL(decryptedBlobUrl);
      }
    } else {
      arrayBufferOut = encryptedArrayBuffer;
    }

    // base64 in 32 KiB slices — String.fromCharCode.apply blows the stack on
    // arrays >~125 K bytes (browser-dependent). Chunked encode is the
    // standard workaround.
    const bytes = new Uint8Array(arrayBufferOut);
    let binary = '';
    const CHUNK = 0x8000;
    for (let i = 0; i < bytes.length; i += CHUNK) {
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
    }
    return { base64: btoa(binary), hadEncryptionInfo: !!encryptionInfo };
  }

}

// Create singleton instance
const matrixService = new MatrixService();

// Expose globally for E2E tests (dev/test builds only — never production)
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  window.__matrixService = matrixService;
  Object.defineProperty(window, '__matrixClient', {
    configurable: true,
    get() {
      return matrixService.client;
    },
  });
  // NED-4397: expose matrixAuthService (dev only) so e2e can capture the login
  // nonce (getLoginNonce()) to build a superseded-session invalidation.
  import('./matrixAuthService')
    .then((m) => { window.__matrixAuthService = m.default; })
    .catch(() => { /* non-fatal for tests */ });
}

export default matrixService;
